import { isEmpty, isPresent } from '@ember/utils';
import { Data, EventDataTransformer, EventTransforms as transforms } from 'cio/utils/analytics/transforms';
import TransactionalMessageModel from 'cio/models/transactional-message';
import RouteInfo from '@ember/routing/-private/route-info';
import SubjectModel from 'cio/models/subject';
import CampaignModel from 'cio/models/campaign';
import { Variant } from 'cio/components/ui-tester';
import { capitalize } from '@ember/string';

type EventTypeNameFunction = (data: Data) => string | undefined;

export type EventType = {
  name: string | EventTypeNameFunction;
  transform?: EventDataTransformer;
  debounce?: (data: Data) => boolean;
};

type EventsList = {
  signup:
    | 'started'
    | 'request_invite_clicked'
    | 'sign_in_clicked'
    | 'testimonial_clicked'
    | 'name_and_email_submitted'
    | 'password_submitted'
    | 'company_info_submitted'
    | 'goal_submitted'
    | 'referral_source_submitted'
    | 'resend_confirmation_clicked'
    | 'role_submitted'
    | 'channel_submitted'
    | 'tool_experience_submitted'
    | 'connecting_data_submitted'
    | 'signup_completed';
  onboarding:
    | 'door_test_clicked'
    | 'door_test_video_clicked'
    | 'door_test_live_clicked'
    | 'door_test_form_submitted'
    | 'pricing_card_button_clicked'
    | 'asked_for_help'
    | 'integrate_viewed'
    | 'engage_viewed'
    | 'configure_viewed'
    | 'account_verification_viewed'
    | 'account_verification_submitted'
    | 'setup_finished'
    | 'integration_details_clicked'
    | 'data_type_clicked'
    | 'setup_channels_clicked'
    | 'skip_clicked'
    | 'setup_link_tracking_in_deliverability_clicked'
    | 'preview_sample_campaign_clicked'
    | 'preview_sample_newsletter_clicked'
    | 'page_viewed'
    | 'setup_guide_product_tour_video_started'
    | 'setup_guide_product_tour_video_loaded'
    | 'setup_guide_product_tour_video_not_included'
    | 'upgrade_clicked';
  import:
    | 'attribute_mapped'
    | 'attribute_skipped'
    | 'upload_started'
    | 'original_file_exported'
    | 'warnings_file_exported'
    | 'event_column_warning'
    | 'errors_file_exported'
    | 'preview_file_exported'
    | 'upload_viewed'
    | 'map_fields_viewed'
    | 'event_preview_viewed'
    | 'review_viewed'
    | 'list_viewed'
    | 'canceled'
    | 'example_link_clicked'
    | 'upload_deleted'
    | 'replace_clicked'
    | 'field_warning_displayed'
    | 'identifier_feedback_started'
    | 'identifier_feedback_completed';
  user:
    | 'error_encountered'
    | 'help_docs_viewed'
    | 'settings_viewed'
    | 'two_fa_viewed'
    | 'settings_edited'
    | 'invite_started'
    | 'support_request_started'
    | 'feedback_request_started'
    | 'branching_request_started'
    | 'billing_history_viewed'
    | 'password_requirements_failed'
    | 'no_accessible_workspaces'
    | 'change_google_account_started'
    | 'unlink_google_account_started'
    | 'sign_in_with_different_email_clicked'
    | 'sign_in_with_google_clicked'
    | 'sign_in_choose_user_list_viewed'
    | 'forgot_password_clicked'
    | 'got_session_expiration_timestamp'
    | 'got_session_expiration_cookie_set'
    | 'got_session_expiration_cookie_removed'
    | 're_authenticated'
    | 'logged_out_session_expired';
  account:
    | 'settings_viewed'
    | 'security_settings_viewed'
    | 'security_sso_settings_viewed'
    | 'security_sso_settings_authenticate_started'
    | 'security_sso_settings_authenticate_submitted'
    | 'security_sso_settings_authenticate_failed'
    | 'security_sso_settings_disable_started'
    | 'billing_receipt_download'
    | 'billing_invoice_download'
    | 'cancellation_started'
    | '2fa_required_enable_started'
    | '2fa_required_disable_started'
    | 'canceled_state_viewed'
    | 'reactivation_started'
    | 'reactivation_completed'
    | 'intro_modal_video_started'
    | 'intro_modal_video_dismissed'
    | 'privacy_viewed'
    | 'privacy_questions_viewed'
    | 'privacy_enable_product_tracking_started'
    | 'privacy_disable_product_tracking_started'
    | 'downgrade_support_request_started'
    | 'your_plan_viewed'
    | 'billing_history_viewed'
    | 'available_plans_viewed'
    | 'payment_methods_viewed';
  campaign:
    | 'archive_started'
    | 'unarchive_started'
    | 'stop_started'
    | 'restart_started'
    | 'delete_started'
    | 'workflow_existing_item_clicked'
    | 'workflow_existing_item_copy_clicked'
    | 'workflow_existing_item_copy_started'
    | 'workflow_existing_item_copy_completed'
    | 'workflow_existing_item_move_started'
    | 'workflow_existing_item_move_completed'
    | 'workflow_existing_item_delete_started'
    | 'workflow_existing_item_delete_confirmed'
    | 'workflow_existing_item_copy_from_started'
    | 'workflow_existing_item_copy_from_completed'
    | 'workflow_existing_item_copy_to_started'
    | 'workflow_existing_item_copy_to_completed'
    | 'workflow_existing_item_copy_from_modal_action_completed'
    | 'workflow_existing_item_copy_to_modal_action_completed'
    | 'workflow_existing_thumbnail_hovered'
    | 'workflow_existing_item_preview_clicked'
    | 'workflow_new_item_add_started'
    | 'workflow_canvas_zoom_changed'
    | 'workflow_tray_tab_changed'
    | 'workflow_metrics_tab_time_period_changed'
    | 'workflow_metrics_tab_chosen_metrics_changed'
    | 'overview_viewed'
    | 'trigger_tab_viewed'
    | 'workflow_tab_viewed'
    | 'sent_tab_viewed'
    | 'drafts_tab_viewed'
    | 'goal_and_exit_tab_viewed'
    | 'settings_tab_viewed'
    | 'metrics_tab_viewed'
    | 'review_tab_viewed'
    | 'journeys_tab_viewed'
    | 'edit_discarded'
    | 'list_viewed'
    | 'trigger_filtered'
    | 'ab_tests_tab_viewed'
    | 'create_started'
    | 'journey_metrics_chart_hover'
    | 'journey_metrics_tooltip_viewed'
    | 'follow_campaign_toggled'
    | 'unfollow_campaign_feedback_submitted'
    | 'volume_performance_metrics_filtered'
    | 'performance_delivery_metrics_chart_hover'
    | 'performance_delivery_metrics_chart_changed'
    | 'journey_metrics_chart_changed'
    | 'metrics_timeframe_filter_changed'
    | 'overview_timeframe_filter_changed'
    | 'overview_chart_hover'
    | 'leaderboard_card_display_changed'
    | 'ab_test_star_icon_hovered'
    | 'message_metrics_table_changed'
    | 'copied_metrics_link'
    | 'copied_metrics_tracked_response'
    | 'add_note_started'
    | 'edit_note_started'
    | 'edit_note_completed'
    | 'note_color_change_started'
    | 'note_color_change_completed'
    | 'move_note_started'
    | 'move_note_completed'
    | 'delete_note_started'
    | 'delete_note_confirmed'
    | 'webhook_url_copied'
    | 'webhook_test_data_received'
    | 'webhook_response_tab_viewed'
    | 'specific_webhook_journey_viewed'
    | 'data_workflow_webhooks_modal_viewed'
    | 'data_workflow_webhooks_modal_clicked'
    | 'for_fun_positivity_mode_changed'
    | 'workflow_image_export_started'
    | 'campaign_backfill_started'
    | 'campaign_backfill_completed'
    | 'empty_state_viewed'
    | 'empty_state_learn_more_clicked'
    | 'empty_state_create_campaign_clicked'
    | 'empty_state_template_selected'
    | 'empty_state_create_from_scratch_selected';
  broadcast:
    | 'edit_started'
    | 'delete_started'
    | 'copy_started'
    | 'copy_to_workspace_started'
    | 'send_started'
    | 'scheduling_edit_started'
    | 'schedule_started'
    | 'sending_cancel_started'
    | 'overview_tab_viewed'
    | 'workflow_tab_viewed'
    | 'recipients_tab_viewed'
    | 'test_tab_viewed'
    | 'content_tab_viewed'
    | 'broadcasts_tab_viewed'
    | 'drafts_tab_viewed'
    | 'goal_tab_viewed'
    | 'triggering_details_tab_viewed'
    | 'review_tab_viewed'
    | 'journeys_tab_viewed'
    | 'list_viewed'
    | 'list_sorted'
    | 'create_started'
    | 'edit_discarded'
    | 'sent_tab_viewed';
  message:
    | 'add_data_started'
    | 'add_data_completed'
    | 'sample_data_profile_changed'
    | 'sample_data_profiles_searched'
    | 'review_links_viewed'
    | 'errors_viewed'
    | 'errors_researched'
    | 'preview_clicked'
    | 'newsletter_edit_content'
    | 'campaign_edit_content'
    | 'url_params_edit_started'
    | 'time_zone_attribute_tested'
    | 'retry_send_canceled'
    | 'retry_send_confirmed'
    | 'retry_send_started'
    | 'minimap_viewed'
    | 'minimap_clicked'
    | 'send_test'
    | 'send_test_error'
    | 'sample_data_attributes_viewed'
    | 'sample_data_relationships_viewed'
    | 'sample_data_objects_changed'
    | 'sample_data_events_viewed'
    | 'sample_data_event_changed'
    | 'exported_html'

    // Parcel triggered events
    | 'html_formatted'
    | 'inspect_element_enabled'
    | 'focus_mode_enabled'
    | 'expanded_table_view_enabled'
    | 'preview_responsiveness_enabled'
    | 'preview_mode_updated'
    | 'preview_layout_updated'
    | 'color_scheme_updated'
    | 'visual_impairment_enabled'
    | 'image_blocking_enabled'
    | 'screenshot_captured'
    | 'links_validated'
    | 'images_validated'
    | 'spamassassin_validated'
    | 'accessibility_validated'
    | 'command_palette_displayed'

    // Visual Editor triggered events
    | 'insert_panel_toggled'
    | 'layers_panel_toggled'
    | 'code_panel_toggled'
    | 'properties_panel_toggled'
    | 'component_added'
    | 'component_converted';
  segment:
    | 'duplicated'
    | 'list_viewed_searched_or_filtered'
    | 'creation_started'
    | 'type_chosen'
    | 'overview_tab_viewed'
    | 'edit_started'
    | 'edit_discarded'
    | 'usage_tab_viewed'
    | 'ad_audiences_tab_viewed'
    | 'segment_membership_timeframe_filter_changed'
    | 'segment_membership_export_timeframe_changed'
    | 'people_tab_viewed'
    | 'segment_membership_chart_hover'
    | 'segment_membership_change_chart_hover'
    | 'segment_condition_selected'
    | 'documentation_viewed'
    | 'json_condition_edited'
    | 'segment_backfill_started'
    | 'segment_backfill_completed';
  people:
    | 'manual_person_creation_started'
    | 'list_viewed_searched_filtered'
    | 'person_attributes_viewed'
    | 'person_overview_viewed'
    | 'person_segments_viewed'
    | 'person_devices_viewed'
    | 'person_drafts_viewed'
    | 'person_relationships_viewed'
    | 'person_sent_viewed'
    | 'person_activity_viewed'
    | 'person_journeys_viewed'
    | 'specific_person_journey_viewed'
    | 'id_tooltip_hovered'
    | 'merge_setup_tab_viewed'
    | 'merge_review_and_confirm_tab_viewed'
    | 'merge_person_searched'
    | 'merge_person_selected'
    | 'merge_person_cleared'
    | 'merge_people_swapped'
    | 'merge_attributes_searched'
    | 'merge_feedback_request_started';
  objects:
    | 'list_viewed_searched_or_filtered'
    | 'business_type_self_selected'
    | 'object_create_started'
    | 'object_displayed_attribute_edit_started'
    | 'object_new_attribute_create_clicked'
    | 'object_overview_tab_viewed'
    | 'object_attributes_tab_viewed'
    | 'object_relationship_tab_viewed'
    | 'object_activity_tab_viewed'
    | 'object_type_list_viewed'
    | 'object_type_predefined_list_viewed'
    | 'object_type_create_started'
    | 'object_type_edit_started'
    | 'object_api_example_copied'
    | 'object_guide_page_link_copied'
    | 'relationships_manual_add_started'
    | 'relationships_manual_add_completed'
    | 'relationships_manual_delete_completed';
  shortcuts: 'opened' | 'clicked' | 'create_started' | 'edit_started' | 'delete_started';
  audience: 'add_network_started' | 'sync_creation_started';
  integration:
    | 'list_viewed'
    | 'list_viewed_public'
    | 'segment_in_success_viewed'
    | 'clicked'
    | 'api_credentials_creation_started'
    | 'api_credentials_deletion_started'
    | 'api_credentials_copied'
    | 'request_started'
    | 'request_completed'
    | 'landing_page_button_clicked'
    | 'request_scheduling_started'
    | 'list_searched'
    | 'list_filtered'
    | 'notify_when_available_clicked'
    | 'use_case_link_clicked'
    | 'tracking_api_keys_viewed'
    | 'app_api_keys_viewed'
    | 'enable_started'
    | 'enabled'
    | 'verify_started'
    | 'verify_completed'
    | 'disable_started'
    | 'disabled'
    | 'clear_started'
    | 'clear_completed'
    | 'reporting_webhook_test_sent'
    | 'reporting_webhook_key_copied'
    | 'edit_started'
    | 'data_campaign_learn_how_clicked'
    | 'data_campaign_video_clicked'
    | 'data_campaign_example_clicked'
    | 'error_encountered';
  email:
    | 'sending_domains_tab_viewed'
    | 'deliverability_domain_viewed'
    | 'deliverability_domain_checking_started'
    | 'link_tracking_domain_viewed'
    | 'link_tracking_domain_checking_started'
    | 'mail_servers_domain_viewed'
    | 'smtp_tab_viewed'
    | 'suppressions_tab_viewed'
    | 'change_layout_clicked'
    | 'change_layout_applied'
    | 'preview_plaintext'
    | 'builder_chosen'
    | 'preview_template_clicked'
    | 'search_performed'
    | 'custom_smtp_add_server_started'
    | 'custom_smtp_add_server_setup_action_complete'
    | 'custom_smtp_add_server_setup_canceled'
    | 'saved_row_started'
    | 'saved_row_canceled'
    | 'saved_row_inserted'
    | 'html_toggled';
  slack: 'settings_viewed' | 'enabling_started';
  twilio: 'settings_viewed';
  in_app:
    | 'settings_viewed'
    | 'enabling_started'
    | 'enabling_completed'
    | 'disabled'
    | 'edit_completed'
    | 'edit_message_clicked'
    | 'builder_chosen';
  push:
    | 'message_settings_viewed'
    | 'configuration_viewed'
    | 'setup_started'
    | 'setup_checklist_viewed'
    | 'authorization_added'
    | 'authorization_updated'
    | 'push_disabled';
  dashboard:
    | 'viewed'
    | 'navigate_away'
    | 'chart_changed'
    | 'chart_hover'
    | 'timeframe_filters_changed'
    | 'leaderboard_card_display_changed'
    | 'tests_card_display_changed'
    | 'links_channel_changed';
  export: 'list_viewed';
  logs:
    | 'viewed'
    | 'deliveries_or_drafts_viewed'
    | 'data_index_viewed'
    | 'data_index_item_viewed'
    | 'specific_activity_viewed'
    | 'more_logs_loaded';
  workspace:
    | 'compare_plans_from_workspaces_viewed'
    | 'creation_started'
    | 'settings_viewed'
    | 'api_credentials_creation_started'
    | 'api_credentials_deletion_started'
    | 'api_credentials_copied'
    | 'tracking_api_keys_viewed'
    | 'app_api_keys_viewed'
    | 'general_settings_viewed'
    | 'switched'
    | 'snippets_viewed'
    | 'language_settings_viewed'
    | 'message_limits_viewed'
    | 'message_limits_edit_started'
    | 'identifier_clicked'
    | 'identifier_change_started'
    | 'identifier_change_submitted'
    | 'anonymous_events_merge_enabled'
    | 'anonymous_events_merge_disabled'
    | 'auto_identify_track_links_enabled'
    | 'auto_identify_track_links_disabled'
    | 'open_tracking_disabled'
    | 'open_tracking_enabled'
    | 'auto_merge_enabled'
    | 'auto_merge_disabled'
    | 'update_identifiers_by_id_changed'
    | 'delivery_settings_updated'
    | 'delivery_email_updated';
  snippets:
    | 'creation_started'
    | 'edit_started'
    | 'delete_started'
    | 'list_searched'
    | 'list_sorted'
    | 'detail_page_viewed'
    | 'list_filtered';
  collections: 'creation_started' | 'delete_started' | 'list_viewed' | 'update_started';
  analysis:
    | 'viewed'
    | 'report_criteria_changed'
    | 'report_started'
    | 'report_completed'
    | 'refine_results_started'
    | 'refine_results_completed'
    | 'display_option_changed'
    | 'chart_hover';
  tags:
    | 'create_started'
    | 'create_canceled'
    | 'delete_started'
    | 'edit_started'
    | 'manage_started'
    | 'manage_completed';
  comments:
    | 'comment_added'
    | 'comment_edited'
    | 'comment_deleted'
    | 'comment_resolved'
    | 'comment_reopened'
    | 'comment_replied';
  transactional:
    | 'join_beta_viewed'
    | 'index_viewed'
    | 'overview_tab_viewed'
    | 'metrics_tab_viewed'
    | 'drafts_tab_viewed'
    | 'sent_tab_viewed'
    | 'create_started'
    | 'edit_started'
    | 'add_content_step_viewed'
    | 'configure_settings_step_viewed'
    | 'send_message_step_viewed'
    | 'code_copied'
    | 'activated'
    | 'transactional_composer_viewed'
    | 'metrics_display_option_changed'
    | 'timeframe_filters_changed'
    | 'delete_started'
    | 'copy_started'
    | 'copy_to_workspace_started'
    | 'hello_world_message_create_started';
  events: 'send_started' | 'send_completed' | 'error_encountered';
  forms:
    | 'list_viewed_or_filtered'
    | 'settings_viewed'
    | 'manage_form_started'
    | 'feedback_request_started'
    | 'form_builders_viewed'
    | 'form_builder_clicked'
    | 'setup_find_form_tab_viewed'
    | 'setup_select_form_tab_viewed'
    | 'custom_form_code_viewed'
    | 'custom_form_code_copied'
    | 'setup_map_form_fields_tab_viewed';
  facebook_lead_ads:
    | 'connect_account_started'
    | 'connect_account_completed'
    | 'connect_business_page_started'
    | 'disconnect_business_page_started'
    | 'conversion_enabled';
  warehouse:
    | 'settings_viewed'
    | 'destination_viewed'
    | 'create_sync_started'
    | 'create_sync_completed'
    | 'export_enabled'
    | 'export_disabled'
    | 'disconnect_sync_started'
    | 'disconnect_sync_completed'
    | 'update_configuration_started'
    | 'update_configuration_completed';
  sql_imports:
    | 'create_started'
    | 'select_database_started'
    | 'add_database_started'
    | 'add_database_failed'
    | 'delete_database_started'
    | 'edit_database_started'
    | 'edit_database_failed'
    | 'write_query_started'
    | 'write_relationships_query_started'
    | 'skip_relationships_query'
    | 'run_query_started'
    | 'run_query_failed'
    | 'sync_settings_started'
    | 'sync_activate_started'
    | 'sync_delete_started'
    | 'sync_pause_started'
    | 'imports_tab_viewed'
    | 'query_tab_viewed'
    | 'relationship_query_tab_viewed'
    | 'preview_timeout_dismiss'
    | 'preview_timeout_continue'
    | 'review_import'
    | 'create_sync_started'
    | 'settings_tab_viewed'
    | 'cancel_import'
    | 'cancel_import_started'
    | 'cancel_import_canceled'
    | 'download_error_file';
  library: 'page_viewed' | 'message_selected' | 'message_parent_selected';
  datacenter: 'learn_more_link_click';
  languages:
    | 'add_language_canceled'
    | 'add_language_clicked'
    | 'delete_language_clicked'
    | 'attribute_setup_canceled'
    | 'different_format_clicked'
    | 'feedback_canceled'
    | 'attribute_info_icon_hovered'
    | 'settings_link_clicked'
    | 'settings_tip_link_clicked'
    | 'language_tab_clicked'
    | 'more_dropdown_clicked';
  experimental_features: 'viewed' | 'toggled';
  asset_library:
    | 'viewed'
    | 'file_previewed'
    | 'file_url_copied'
    | 'file_delete_started'
    | 'folder_add_canceled'
    | 'folder_add_started'
    | 'folder_viewed'
    | 'folder_delete_started'
    | 'bulk_delete_started'
    | 'upload_image_started';
  subscription_center:
    | 'topics_viewed'
    | 'branding_page_viewed'
    | 'add_topic_started'
    | 'delete_topic_started'
    | 'edit_topic_started'
    | 'preview_clicked'
    | 'preview_draft_clicked'
    | 'example_clicked'
    | 'activation_completed'
    | 'deactivation_completed'
    | 'get_started_clicked'
    | 'settings_page_viewed'
    | 'branding_updated'
    | 'branding_exited'
    | 'learn_more_clicked'
    | 'add_languages_started'
    | 'edit_languages_started'
    | 'edit_languages_submitted'
    | 'language_edit_started'
    | 'language_edited'
    | 'language_deleted'
    | 'language_status_changed'
    | 'set_bulk_preferences_started'
    | 'set_bulk_preferences_submitted'
    | 'set_bulk_preferences_filtered';
  plan_billing:
    | 'switch_to_premium_button_clicked'
    | 'essentials_plan_post_checkout_viewed'
    | 'premium_plan_post_checkout_viewed'
    | 'included_in_plan_viewed'
    | 'view_all_features_clicked'
    | 'sales_or_contracts_lets_talk_clicked'
    | 'buy_or_request_access_clicked'
    | 'plan_action_clicked'
    | 'your_plan_available_plan_cta_clicked'
    | 'limits_banner_learn_more_clicked'
    | 'start_journeys_trial_clicked'
    | 'available_plans_section_changed'
    | 'view_billing_help'
    | 'start_cdp_starter_subscription_clicked';
  help_center:
    | 'menu_item_clicked'
    | 'docs_search_input'
    | 'docs_searched_duration'
    | 'docs_search_results_clicked'
    | 'contextual_help_item_clicked'
    | 'ai_powered_search_banner_clicked';
  inline_support:
    | 'inline_support_opened'
    | 'inline_support_closed'
    | 'inline_support_next_page_clicked'
    | 'inline_support_previous_page_clicked'
    | 'inline_support_cancel_clicked'
    | 'inline_support_cancel_dismissed'
    | 'inline_support_cancel_confirmed'
    | 'inline_support_page_viewed'
    | 'inline_support_docs_suggestion_clicked'
    | 'inline_support_field_input'
    | 'inline_support_support_access_granted'
    | 'inline_support_support_access_error'
    | 'inline_support_submit_clicked'
    | 'inline_support_submit_error'
    | 'inline_support_submit_success';
  inline_contracts_sales:
    | 'inline_contracts_sales_form_opened'
    | 'inline_contracts_sales_form_closed'
    | 'inline_contracts_sales_form_field_input'
    | 'inline_contracts_sales_form_cancel_clicked'
    | 'inline_contracts_sales_form_cancel_dismissed'
    | 'inline_contracts_sales_form_submit_clicked'
    | 'inline_contracts_sales_form_submit_error'
    | 'inline_contracts_sales_form_submit_success';
  objects_liquid:
    | 'generate_clicked'
    | 'subject_copy_clicked'
    | 'subject_copied'
    | 'body_copy_clicked'
    | 'body_copied'
    | 'adjust_clicked';

  renderer: 'version_change_started' | 'version_change_completed';
  ui_test: Variant.A | Variant.B;
  product_activation: 'activation_started' | 'activation_completed' | 'activation_step_completed';
  performance: 'initial_load' | 'signup';
  test_delivery_mode: 'popover_opened' | 'popover_cta_clicked';
};

export type EventsType = { [K in keyof EventsList]: { [key in EventsList[K]]: EventType } };

export const Events: EventsType = {
  signup: {
    started: {
      name: 'Signup: Started',
      transform: transforms.signup.signupType,
    },
    name_and_email_submitted: {
      name: 'Signup: Name and Email Submitted',
      transform: transforms.signup.signupType,
    },
    password_submitted: {
      name: 'Signup: Password Submitted',
      transform: transforms.signup.signupType,
    },
    company_info_submitted: {
      name: 'Signup: Company Info and Terms Submitted',
      transform: transforms.signup.signupType,
    },
    goal_submitted: {
      name: 'Signup: Goal Submitted',
      transform: transforms.signup.goalSubmitted,
    },
    referral_source_submitted: {
      name: 'Signup: Referral Source Submitted',
      transform: transforms.signup.referralSourceSubmitted,
    },
    resend_confirmation_clicked: {
      name: 'Signup: Resend Confirmation Clicked',
    },
    request_invite_clicked: {
      name: 'Signup: Request an invite Clicked',
      transform: transforms.signup.signupType,
    },
    sign_in_clicked: {
      name: 'Signup: Sign in Clicked',
      transform: transforms.signup.signupType,
    },
    testimonial_clicked: {
      name: 'Signup: Testimonial Clicked',
    },
    role_submitted: {
      name: 'Signup: Role Submitted',
      transform: transforms.signup.roleSubmitted,
    },
    channel_submitted: {
      name: 'Signup: Channels Submitted',
      transform: transforms.signup.channelsSubmitted,
    },
    tool_experience_submitted: {
      name: 'Signup: Tool Experience Submitted',
      transform: transforms.signup.toolExperienceSubmitted,
    },
    connecting_data_submitted: {
      name: 'Signup: Connecting Data Submitted',
      transform: transforms.signup.connectingDataSubmitted,
    },
    signup_completed: {
      name: 'Signup: Completed',
      transform: transforms.signup.signupType,
    },
  },
  onboarding: {
    door_test_clicked: {
      name: 'Onboarding Door Test: Clicked',
      transform: transforms.onboarding.trackAccount,
    },
    door_test_video_clicked: {
      name: 'Onboarding Door Test: Video Clicked',
      transform: transforms.onboarding.trackAccount,
    },
    door_test_live_clicked: {
      name: 'Onboarding Door Test: Live Clicked',
      transform: transforms.onboarding.trackAccount,
    },
    door_test_form_submitted: {
      name: 'Onboarding Door Test: Form Submitted',
      transform: transforms.onboarding.trackAccount,
    },
    pricing_card_button_clicked: {
      name: 'Onboarding: Pricing Card Button Clicked',
      transform: transforms.onboarding.pricingCardButtonClick,
    },
    asked_for_help: {
      name: 'Onboarding: Ask for Help Clicked',
    },
    integrate_viewed: {
      name: "Onboarding: 'Bring in your data' Section Viewed",
    },
    engage_viewed: {
      name: "Onboarding: 'Start sending' Section Viewed",
    },
    configure_viewed: {
      name: "Onboarding: 'Learn more' Section Viewed",
    },
    account_verification_viewed: {
      name: 'Onboarding: Account Verification Viewed',
    },
    account_verification_submitted: {
      name: 'Onboarding: Account Verification Submitted',
    },
    setup_finished: {
      name: 'Onboarding: Setup Finished',
    },
    integration_details_clicked: {
      name: 'Onboarding: Integration Details Clicked',
      transform: transforms.onboarding.detailsClicked,
    },
    data_type_clicked: {
      name: 'Onboarding: Data Type Clicked',
      transform: transforms.onboarding.dataTypeClicked,
    },
    setup_channels_clicked: {
      name: 'Onboarding: Setup My Channels Clicked',
      transform: transforms.onboarding.setupChannelsClicked,
    },
    skip_clicked: {
      name: 'Onboarding: Skip Clicked',
      transform: transforms.onboarding.stepSkipped,
    },
    setup_link_tracking_in_deliverability_clicked: {
      name: 'Onboarding: Setup Link Tracking in Deliverability Settings Clicked',
    },
    preview_sample_campaign_clicked: {
      name: 'Onboarding: Preview Sample Campaign Clicked',
      transform: transforms.campaign.base,
    },
    preview_sample_newsletter_clicked: {
      name: 'Onboarding: Preview Sample Newsletter Clicked',
      transform: transforms.broadcast.base,
    },
    page_viewed: {
      name: 'Onboarding: Page Viewed',
      transform: transforms.onboarding.pageViewed,
    },
    setup_guide_product_tour_video_started: {
      name: 'Onboarding: Setup Guide Product Tour Video Started',
      transform: transforms.onboarding.setupGuideProductTourVideoStarted,
    },
    setup_guide_product_tour_video_loaded: {
      name: 'Onboarding: Setup Guide Product Tour Video Loaded',
      transform: transforms.onboarding.setupGuideProductTourVideoLoaded,
    },
    setup_guide_product_tour_video_not_included: {
      name: 'Onboarding: Setup Guide Product Tour Video Not Included',
      transform: transforms.onboarding.setupGuideProductTourVideoNotIncluded,
    },
    upgrade_clicked: {
      name: 'Onboarding: Trial Indicator Upgrade Clicked',
      transform: transforms.onboarding.upgradeClicked,
    },
  },
  import: {
    attribute_mapped: {
      name: 'Import: Attribute Mapped Column Changed',
      transform: transforms.import.attributeMapped,
    },
    attribute_skipped: {
      name: 'Import: Attribute Mapped Column Changed',
      transform: transforms.import.attributeSkipped,
    },
    upload_started: {
      name: 'Import: Upload Started',
      transform: transforms.import.upload,
    },
    original_file_exported: {
      name: 'Import: Original File Exported',
      transform: transforms.import.fileExport,
    },
    warnings_file_exported: {
      name: 'Import: Warnings File Exported',
      transform: transforms.import.fileExport,
    },
    event_column_warning: {
      name: 'Import: Event Column Warning',
      transform: transforms.import.eventColumnWarning,
    },
    errors_file_exported: {
      name: 'Import: Errors File Exported',
      transform: transforms.import.fileExport,
    },
    preview_file_exported: {
      name: 'Import: Final Import Previewed or Downloaded',
      transform: transforms.import.preview,
    },
    upload_viewed: {
      name: 'Import: Upload CSV Tab Viewed',
      transform: transforms.import.routeView,
    },
    map_fields_viewed: {
      name: 'Import: Map Fields Tab Viewed',
      transform: transforms.import.routeView,
    },
    event_preview_viewed: {
      name: 'Import: Event Preview Tab Viewed',
      transform: transforms.import.routeView,
    },
    review_viewed: {
      name: 'Import: Review Tab Viewed',
      transform: transforms.import.reviewRouteView,
    },
    list_viewed: {
      name: 'Import: List Viewed',
    },
    canceled: {
      name: 'Import: Canceled',
      transform: transforms.import.canceled,
    },
    example_link_clicked: {
      name: 'Import: Example File Clicked',
      transform: transforms.import.routeView,
    },
    upload_deleted: {
      name: 'Import: Upload Deleted',
      transform: transforms.import.uploadChanged,
    },
    replace_clicked: {
      name: 'Import: Replace Clicked',
      transform: transforms.import.uploadChanged,
    },
    field_warning_displayed: {
      name: 'Import: Field Warning Displayed',
      transform: transforms.import.fieldWarningDisplayed,
    },
    identifier_feedback_started: {
      name: 'Import: Identifier Feedback Request Started',
      transform: transforms.import.base,
    },
    identifier_feedback_completed: {
      name: 'Import: Identifier Feedback Request Completed',
      transform: transforms.import.identifierFeedbackCompleted,
    },
  },
  user: {
    error_encountered: {
      name: 'User: Error Encountered',
      transform: transforms.user.errorEncountered,
    },
    help_docs_viewed: {
      name: 'User: Help Docs Viewed',
      transform: transforms.user.helpDocsView,
    },
    settings_viewed: {
      name: 'User: Personal Settings Viewed',
    },
    two_fa_viewed: {
      name: 'User: 2FA Enabling Started',
    },
    settings_edited: {
      name: 'User: Settings Edited',
    },
    invite_started: {
      name: 'User: Team Member Invite Started',
    },
    support_request_started: {
      name: 'User: Support Request Started',
    },
    feedback_request_started: {
      name: 'User: Feedback Request Started',
      transform: transforms.user.feedbackRequestStarted,
    },
    branching_request_started: {
      name: 'User: Feedback Request Started',
    },
    billing_history_viewed: {
      name: 'User: Billing History Viewed',
    },
    password_requirements_failed: {
      name: 'User: Password Requirements Failed',
      transform: transforms.user.passwordError,
    },
    no_accessible_workspaces: {
      name: 'User: No Workspaces Page Viewed',
    },
    change_google_account_started: {
      name: 'User: Change Google Account Started',
    },
    unlink_google_account_started: {
      name: 'User: Unlink Google Account Started',
    },
    sign_in_with_different_email_clicked: {
      name: 'User: Sign in With Different Email Clicked',
    },
    sign_in_with_google_clicked: {
      name: 'User: Sign in With Google Clicked',
    },
    sign_in_choose_user_list_viewed: {
      name: 'User: Choose User List Viewed',
      transform: transforms.user.chooseUserListViewed,
    },
    forgot_password_clicked: {
      name: 'User: Forgot Password Clicked',
      transform: transforms.user.forgotPasswordClicked,
    },
    got_session_expiration_timestamp: {
      name: 'User: Got Session Expiration Timestamp Set',
      transform: transforms.user.gotSessionExpirationTimestamp,
    },
    got_session_expiration_cookie_set: {
      name: 'User: Got Session Expiration Cookie Set',
      transform: transforms.user.gotSessionExpirationCookieSet,
    },
    got_session_expiration_cookie_removed: {
      name: 'User: Got Session Expiration Cookie Removed',
      transform: transforms.user.gotSessionExpirationCookieRemoved,
    },
    re_authenticated: {
      name: 'User: Re Authenticated',
      transform: transforms.user.reauthenticated,
    },
    logged_out_session_expired: {
      name: 'User: Automatically Logged Out Session Expired',
    },
  },
  account: {
    settings_viewed: {
      name: 'Account: Settings Viewed',
    },
    security_settings_viewed: {
      name: 'Account: Security Settings Viewed',
    },
    security_sso_settings_viewed: {
      name: 'Account: Configure SSO Viewed',
    },
    security_sso_settings_authenticate_started: {
      name: 'Account: Authenticate SSO Started',
      transform: transforms.account.ssoIdentityProvider,
    },
    security_sso_settings_authenticate_submitted: {
      name: 'Account: Authenticate SSO Submitted',
      transform: transforms.account.ssoIdentityProvider,
    },
    security_sso_settings_authenticate_failed: {
      name: 'Account: Authenticate SSO Failed',
      transform: transforms.account.ssoIdentityProvider,
    },
    security_sso_settings_disable_started: {
      name: 'Account: Disable SSO Started',
      transform: transforms.account.ssoIdentityProvider,
    },
    billing_receipt_download: {
      name: 'Account: Billing History Downloaded',
    },
    billing_invoice_download: {
      name: 'Account: Billing Invoice Downloaded',
    },
    cancellation_started: {
      name: 'Account: Cancellation Started',
    },
    '2fa_required_enable_started': {
      name: 'Account: Enable Require 2FA Started',
    },
    '2fa_required_disable_started': {
      name: 'Account: Disable Require 2FA Started',
    },
    canceled_state_viewed: {
      name: 'Account: Canceled State Viewed',
      transform: transforms.account.canceledStateViewed,
    },
    reactivation_started: {
      name: 'Account: Reactivation Started',
    },
    reactivation_completed: {
      name: 'Account: Reactivation Completed',
    },
    intro_modal_video_started: {
      name: 'Account: Intro Modal Video Started',
      transform: transforms.account.introModal,
    },
    intro_modal_video_dismissed: {
      name: 'Account: Intro Modal Video Dismissed',
      transform: transforms.account.introModal,
    },
    privacy_viewed: {
      name: 'Account: Privacy and Data Viewed',
    },
    privacy_questions_viewed: {
      name: 'Account: Privacy and Data FAQ Viewed',
    },
    privacy_enable_product_tracking_started: {
      name: 'Account: Enable Product Tracking Started',
    },
    privacy_disable_product_tracking_started: {
      name: 'Account: Disable Product Tracking Started',
    },
    downgrade_support_request_started: {
      name: 'Account: Downgrade Support Request Started',
    },
    your_plan_viewed: {
      name: 'Account: Your Plan Viewed',
    },
    billing_history_viewed: {
      name: 'Account: Billing History Viewed',
    },
    available_plans_viewed: {
      name: 'Account: Available Plans Viewed',
    },
    payment_methods_viewed: {
      name: 'Account: Payment Methods Viewed',
    },
  },
  campaign: {
    archive_started: {
      name: 'Campaign: Archive Started',
      transform: transforms.campaign.stopOrArchiveStart,
    },
    unarchive_started: {
      name: 'Campaign: Unarchive Started',
      transform: transforms.campaign.base,
    },
    stop_started: {
      name: 'Campaign: Stop Started',
      transform: transforms.campaign.stopOrArchiveStart,
    },
    restart_started: {
      name: 'Campaign: Restart Started',
      transform: transforms.campaign.base,
    },
    delete_started: {
      name: 'Campaign: Delete Started',
      transform: transforms.campaign.base,
    },
    workflow_existing_item_clicked: {
      name: 'Campaign: Workflow Existing Item Clicked',
      transform: transforms.campaign.existingItem,
    },
    workflow_existing_item_copy_clicked: {
      name: 'Campaign: Workflow Existing Item Copy Clicked',
      transform: transforms.campaign.base,
    },
    workflow_existing_item_copy_started: {
      name: 'Campaign: Workflow Existing Item Copy Started',
      transform: transforms.campaign.existingItemOperation,
    },
    workflow_existing_item_copy_completed: {
      name: 'Campaign: Workflow Existing Item Copy Completed',
      transform: transforms.campaign.existingItemOperation,
    },
    workflow_existing_item_move_started: {
      name: 'Campaign: Workflow Existing Item Move Started',
      transform: transforms.campaign.existingItemOperation,
    },
    workflow_existing_item_move_completed: {
      name: 'Campaign: Workflow Existing Item Move Completed',
      transform: transforms.campaign.existingItemOperation,
    },
    workflow_existing_item_delete_started: {
      name: 'Campaign: Workflow Existing Item Delete Started',
      transform: transforms.campaign.existingItemOperation,
    },
    workflow_existing_item_delete_confirmed: {
      name: 'Campaign: Workflow Existing Item Delete Confirmed',
      transform: transforms.campaign.existingItemOperation,
    },
    workflow_existing_item_copy_from_started: {
      name: 'Campaign: Workflow Existing Item Copy From Started',
      transform: transforms.campaign.base,
    },
    workflow_existing_item_copy_from_completed: {
      name: 'Campaign: Workflow Existing Item Copy From Completed',
      transform: transforms.campaign.existingItemCopyFromOrToCompleted,
    },
    workflow_existing_item_copy_to_started: {
      name: 'Campaign: Workflow Existing Item Copy To Started',
      transform: transforms.campaign.existingItemCopyToStarted,
    },
    workflow_existing_item_copy_to_completed: {
      name: 'Campaign: Workflow Existing Item Copy To Completed',
      transform: transforms.campaign.existingItemCopyFromOrToCompleted,
    },
    workflow_existing_item_copy_from_modal_action_completed: {
      name: 'Campaign: Workflow Existing Item Copy From Modal Action Completed',
      transform: transforms.campaign.existingItemCopyModalActionCompleted,
    },
    workflow_existing_item_copy_to_modal_action_completed: {
      name: 'Campaign: Workflow Existing Item Copy To Modal Action Completed',
      transform: transforms.campaign.existingItemCopyModalActionCompleted,
    },
    workflow_new_item_add_started: {
      name: 'Campaign: Workflow New Item Add Started',
      transform: transforms.campaign.newWorkflowItem,
    },
    workflow_canvas_zoom_changed: {
      name: 'Campaign: Workflow Canvas Zoom Changed',
      transform: transforms.campaign.zoom,
    },
    workflow_tray_tab_changed: {
      name: 'Campaign: Workflow Tray Tab Changed',
      transform: transforms.campaign.trayTabChanged,
    },
    workflow_metrics_tab_time_period_changed: {
      name: 'Campaign: Workflow Metrics Tab Time Period Changed',
      transform: transforms.campaign.trayMetricsTab,
    },
    workflow_metrics_tab_chosen_metrics_changed: {
      name: 'Campaign: Workflow Metrics Tab Chosen Metrics Changed',
      transform: transforms.campaign.trayMetricsTab,
    },
    workflow_existing_thumbnail_hovered: {
      name: 'Campaign: Workflow Existing Thumbnail Hovered',
      transform: transforms.campaign.existingItemPreview,
    },
    workflow_existing_item_preview_clicked: {
      name: 'Campaign: Workflow Existing Item Preview Clicked',
      transform: transforms.campaign.existingItemPreview,
    },
    overview_viewed: {
      name: 'Campaign: Overview Viewed',
      transform: transforms.campaign.base,
    },
    trigger_tab_viewed: {
      name: 'Campaign: Trigger Tab Viewed',
      transform: transforms.campaign.triggerTabViewed,
    },
    workflow_tab_viewed: {
      name: 'Campaign: Workflow Tab Viewed',
      transform: transforms.campaign.base,
    },
    sent_tab_viewed: {
      name: 'Campaign: Sent Tab Viewed',
      transform: transforms.campaign.base,
    },
    drafts_tab_viewed: {
      name: 'Campaign: Drafts Tab Viewed',
      transform: transforms.campaign.base,
    },
    goal_and_exit_tab_viewed: {
      name: 'Campaign: Goal and Exit Tab Viewed',
      transform: transforms.campaign.base,
    },
    settings_tab_viewed: {
      name: 'Campaign: Settings Tab Viewed',
      transform: transforms.campaign.base,
    },
    metrics_tab_viewed: {
      name: 'Campaign: Metrics Viewed',
      transform: transforms.campaign.base,
    },
    review_tab_viewed: {
      name: 'Campaign: Review Tab Viewed',
      transform: transforms.campaign.reviewRouteView,
    },
    journeys_tab_viewed: {
      name: 'Campaign: Journeys Tab Viewed',
      transform: transforms.campaign.base,
    },
    specific_webhook_journey_viewed: {
      name: 'Campaign: Specific Webhook Journey Viewed',
      transform(data: Data) {
        let campaignModel = (data.model as SubjectModel).belongsTo('campaign').value();
        return transforms.campaign.base({
          ...data,
          model: campaignModel as CampaignModel,
        });
      },
    },
    list_viewed: {
      name: 'Campaign: List Viewed, Searched, or Filtered',
      transform: transforms.campaign.listView,
      debounce(data) {
        return data.route ? !isEmpty(data.route.queryParams.searchString) : false;
      },
    },
    trigger_filtered: {
      name: 'Campaign: Trigger List Filtered or Searched',
      transform: transforms.campaign.triggerFiltered,
    },
    ab_tests_tab_viewed: {
      name: 'Campaign: A/B Tests Tab Viewed',
      transform: transforms.campaign.base,
    },
    create_started: {
      name: 'Campaign: Create Started',
      transform(data: Data) {
        if (isEmpty(data.source)) {
          return {};
        }

        return {
          Source: data.source,
        };
      },
    },
    edit_discarded: {
      name: 'Campaign: Edit(s) Discarded',
      transform: transforms.campaign.editDiscard,
    },
    journey_metrics_chart_hover: {
      name: 'Campaign: Journey Metrics Chart Hover',
      transform: transforms.campaign.metricsChartHover,
    },
    journey_metrics_tooltip_viewed: {
      name: 'Campaign: Journey Metrics Tooltip Viewed',
      transform: transforms.campaign.base,
    },
    follow_campaign_toggled: {
      name: 'Campaign: Follow Campaign Toggled',
      transform: transforms.campaign.followCampaignToggled,
    },
    unfollow_campaign_feedback_submitted: {
      name: 'Campaign: Unfollow Campaign Feedback Submitted',
      transform: transforms.campaign.unfollowCampaignFeedback,
    },
    volume_performance_metrics_filtered: {
      name: 'Campaign: Volume/Performance Metrics Filtered',
      transform: transforms.campaign.volumePerformanceMetricsFiltered,
    },
    performance_delivery_metrics_chart_hover: {
      name: 'Campaign: Performance and Delivery Metrics Chart Hover',
      transform: transforms.campaign.metricsChartHover,
    },
    performance_delivery_metrics_chart_changed: {
      name: 'Campaign: Performance and Delivery Metrics Chart Changed',
      transform: transforms.campaign.performanceDeliveryChartChanged,
    },
    journey_metrics_chart_changed: {
      name: 'Campaign: Journey Metrics Chart Changed',
      transform: transforms.campaign.journeyMetricsChartChanged,
    },
    metrics_timeframe_filter_changed: {
      name: 'Campaign: Metrics Timeframe Filter Changed',
      transform: transforms.campaign.metricsTimeframeFilterChanged,
    },
    overview_timeframe_filter_changed: {
      name: 'Campaign: Overview Timeframe Filter Changed',
      transform: transforms.campaign.overviewTimeframeFilterChanged,
    },
    overview_chart_hover: {
      name: 'Campaign: Overview Chart Hover',
      transform: transforms.campaign.overviewChartHover,
    },
    leaderboard_card_display_changed: {
      name: 'Campaign: Leaderboard Card Display Changed',
      transform: transforms.campaign.leaderboardCardDisplayChanged,
    },
    ab_test_star_icon_hovered: {
      name: 'Campaign: A/B Test Star Icon Hovered',
      transform: transforms.campaign.abTestStarIconHovered,
    },
    message_metrics_table_changed: {
      name: 'Campaign: Message Metrics Table Changed',
      transform: transforms.campaign.messageMetricsTableChanged,
    },
    copied_metrics_link: {
      name: 'Campaign: Copied Metrics Link',
      transform: transforms.campaign.base,
    },
    copied_metrics_tracked_response: {
      name: 'Campaign: Copied Metrics Tracked Response',
      transform: transforms.campaign.base,
    },
    add_note_started: {
      name: 'Campaign: Add Note Started',
      transform: transforms.campaign.base,
    },
    edit_note_started: {
      name: 'Campaign: Edit Note Started',
      transform: transforms.campaign.note,
    },
    edit_note_completed: {
      name: 'Campaign: Edit Note Completed',
      transform: transforms.campaign.note,
    },
    note_color_change_started: {
      name: 'Campaign: Note Color Change Started',
      transform: transforms.campaign.noteColorStarted,
    },
    note_color_change_completed: {
      name: 'Campaign: Note Color Change Completed',
      transform: transforms.campaign.noteColorCompleted,
    },
    move_note_started: {
      name: 'Campaign: Move Note Started',
      transform: transforms.campaign.note,
    },
    move_note_completed: {
      name: 'Campaign: Move Note Completed',
      transform: transforms.campaign.note,
    },
    delete_note_started: {
      name: 'Campaign: Delete Note Started',
      transform: transforms.campaign.note,
    },
    delete_note_confirmed: {
      name: 'Campaign: Delete Note Confirmed',
      transform: transforms.campaign.note,
    },
    webhook_url_copied: {
      name: 'Campaign: Webhook URL Copied',
      transform: transforms.campaign.webhookUrlBase,
    },
    webhook_test_data_received: {
      name: 'Campaign: Webhook Test Data Received',
      transform: transforms.campaign.webhookDataReceived,
    },
    webhook_response_tab_viewed: {
      name: 'Campaign: Webhook Response Tab Viewed',
      transform: transforms.campaign.base,
    },
    data_workflow_webhooks_modal_viewed: {
      name: 'Campaign: Data Workflow Webhooks Modal Viewed',
    },
    data_workflow_webhooks_modal_clicked: {
      name: 'Campaign: Data Workflow Webhooks Modal Clicked',
      transform(data: Data) {
        if (data.destination != null) {
          return {
            'Destination URL': data.destination,
          };
        }

        if (data.video) {
          return {
            'Video Clicked': true,
          };
        }

        return {};
      },
    },
    for_fun_positivity_mode_changed: {
      name: 'For Fun: Positivity Mode Changed',
      transform: transforms.campaign.positivityModeChanged,
    },
    workflow_image_export_started: {
      name: 'Campaign: Workflow Image Export Started',
      transform: transforms.campaign.workflowImageExport,
    },
    campaign_backfill_started: {
      name: 'Campaign: Campaign Backfill Started',
    },
    campaign_backfill_completed: {
      name: 'Campaign: Campaign Backfill Completed',
      transform: transforms.campaign.backfillCompleted,
    },
    empty_state_viewed: {
      name: 'Campaign: Empty State Viewed',
    },
    empty_state_learn_more_clicked: {
      name: 'Campaign: Empty State Learn More Clicked',
    },
    empty_state_create_campaign_clicked: {
      name: 'Campaign: Empty State Create Campaign Clicked',
    },
    empty_state_template_selected: {
      name: 'Campaign: Empty State Template Selected',
      transform: transforms.campaign.emptyStateSelectTemplate,
    },
    empty_state_create_from_scratch_selected: {
      name: 'Campaign: Empty State Create From Scratch Selected',
    },
  },
  broadcast: {
    edit_started: {
      name: 'Newsletter: Edit Started',
      transform: transforms.broadcast.editStarted,
    },
    delete_started: {
      name: 'Newsletter: Delete Started',
      transform: transforms.broadcast.newsletter,
    },
    copy_started: {
      name: 'Newsletter: Copy Started',
      transform: transforms.broadcast.newsletterCopyStarted,
    },
    copy_to_workspace_started: {
      name: 'Newsletter: Copy To Workspace Started',
      transform: transforms.broadcast.newsletterCopyStarted,
    },
    scheduling_edit_started: {
      name: 'Newsletter: Scheduling Edit Started',
      transform: transforms.broadcast.newsletter,
    },
    send_started: {
      name: 'Newsletter: Scheduling or Immediate Sending Started',
      transform: transforms.broadcast.immediateSend,
    },
    schedule_started: {
      name: 'Newsletter: Scheduling or Immediate Sending Started',
      transform: transforms.broadcast.scheduledSend,
    },
    sending_cancel_started: {
      name: 'Newsletter: Sending Cancel Started',
      transform: transforms.broadcast.base,
    },
    sent_tab_viewed: {
      name: 'Newsletter: Sent Tab Viewed',
      transform: transforms.broadcast.base,
    },
    edit_discarded: {
      name: 'Newsletter: Edit(s) Discarded',
      transform: transforms.broadcast.editDiscard,
    },
    content_tab_viewed: {
      name: 'Newsletter: Content Tab Viewed',
      transform: transforms.broadcast.newsletterWithSent,
    },
    test_tab_viewed: {
      name: 'Newsletter: Test Tab Viewed',
      transform: transforms.broadcast.newsletterTestTabViewed,
    },
    overview_tab_viewed: {
      name: 'Broadcast: Overview Tab Viewed',
      transform: transforms.broadcast.baseWithType,
    },
    workflow_tab_viewed: {
      name: 'Broadcast: Workflow Tab Viewed',
      transform: transforms.broadcast.baseWithType,
    },
    recipients_tab_viewed: {
      name: 'Broadcast: Recipients Tab Viewed',
      transform: transforms.broadcast.baseWithSent,
    },
    broadcasts_tab_viewed: {
      name: 'Broadcast: Broadcasts Tab Viewed',
      transform: transforms.broadcast.base,
    },
    drafts_tab_viewed: {
      name: 'Broadcast: Drafts Tab Viewed',
      transform: transforms.broadcast.base,
    },
    goal_tab_viewed: {
      name: 'Broadcast: Goal Tab Viewed',
      transform: transforms.broadcast.baseWithType,
    },
    triggering_details_tab_viewed: {
      name: 'Broadcast: Triggering Details Tab Viewed',
      transform: transforms.broadcast.base,
    },
    journeys_tab_viewed: {
      name: 'Broadcast: Journeys Tab Viewed',
      transform: transforms.broadcast.base,
    },
    review_tab_viewed: {
      name: 'Broadcast: Review Tab Viewed',
      transform: transforms.broadcast.reviewTabViewed,
    },
    list_viewed: {
      name: 'Broadcast: List Viewed',
      transform: transforms.broadcast.listView,
    },
    list_sorted: {
      name: 'Broadcast: List Sorted',
      transform: transforms.broadcast.listSorted,
    },
    create_started: {
      name: 'Broadcast: Create Started',
      transform(data: Data) {
        if (isEmpty(data.source)) {
          return {};
        }

        return {
          Source: data.source,
        };
      },
    },
  },
  message: {
    add_data_started: {
      name: 'Message: Add Data Started',
      transform: transforms.message.addDataStarted,
    },
    add_data_completed: {
      name: 'Message: Add Data Completed',
      transform: transforms.message.addDataCompleted,
    },
    sample_data_profile_changed: {
      name: 'Message: Sample Data Profile Changed',
      transform: transforms.message.messageMultiLanguageData,
    },
    sample_data_profiles_searched: {
      name: 'Message: Sample Data Profiles Searched',
      transform: transforms.message.messageMultiLanguageData,
    },
    review_links_viewed: {
      name: 'Message: Review Links Viewed',
      transform: transforms.message.messageMultiLanguageData,
    },
    errors_viewed: {
      name: 'Message: Errors Viewed',
      transform: transforms.message.errors,
    },
    errors_researched: {
      name: 'Message: Errors Researched',
      transform: transforms.message.errors,
    },
    preview_clicked: {
      name: 'Message: Preview Clicked',
      transform: transforms.message.preview,
    },
    newsletter_edit_content: {
      name: 'Email: Edit Started',
      transform: transforms.message.template,
    },
    campaign_edit_content: {
      name({ model }) {
        // This event is triggered from the `composer.action` route, so the
        // model is an action.

        // We want to send different events from the same route based on the
        // action.  For all event names, the format is ${type}: Edit Started.
        // For example:
        //
        // Email: Edit Started
        // Push: Edit Started
        //
        // ... etc.
        //
        // But for attribute updates, it's "Attribute: Campaign Item Edit Started"
        if (model?.isAttributeAction) {
          return `Attribute: Campaign Item Edit Started`;
        } else {
          return `${model!.info.analyticsName}: Edit Started`;
        }
      },
      transform: transforms.message.action,
    },
    time_zone_attribute_tested: {
      name: 'Message: Time Zone Attribute Tested',
    },
    url_params_edit_started: {
      name: 'Message: URL Parameters Edit Started',
    },
    retry_send_canceled: {
      name: 'Message: Retry Send Canceled',
      transform: transforms.message.retrySend,
    },
    retry_send_confirmed: {
      name: 'Message: Retry Send Confirmed',
      transform: transforms.message.retrySendConfirmed,
    },
    retry_send_started: {
      name: 'Message: Retry Send Started',
      transform: transforms.message.retrySend,
    },
    minimap_viewed: {
      name: 'Message: Minimap Viewed',
      transform: transforms.message.minimapViewed,
    },
    minimap_clicked: {
      name: 'Message: Minimap Clicked',
      transform: transforms.message.minimapClicked,
    },
    send_test: {
      name: 'Message: Send Test',
      transform: transforms.message.sendTest,
    },
    send_test_error: {
      name: 'Message: Send Test Error',
      transform: transforms.message.sendTestError,
    },
    sample_data_attributes_viewed: {
      name: 'Message: Sample Data Attributes Viewed',
    },
    sample_data_relationships_viewed: {
      name: 'Message: Sample Data Relationships Viewed',
    },
    sample_data_objects_changed: {
      name: 'Message: Sample Data Object Changed',
    },
    sample_data_events_viewed: {
      name: 'Message: Sample Data Events Viewed',
    },
    sample_data_event_changed: {
      name: 'Message: Sample Data Event Changed',
    },
    exported_html: {
      name: 'Message: Exported HTML',
      transform: transforms.message.sendTest,
    },

    // Parcel triggered events
    html_formatted: {
      name: 'Message: HTML Formatted',
      transform: transforms.message.parcelData,
    },
    inspect_element_enabled: {
      name: 'Message: Inspect Element Enabled',
      transform: transforms.message.parcelData,
    },
    focus_mode_enabled: {
      name: 'Message: Focus Mode Enabled',
      transform: transforms.message.parcelData,
    },
    expanded_table_view_enabled: {
      name: 'Message: Expanded Table View Enabled',
      transform: transforms.message.parcelData,
    },
    preview_responsiveness_enabled: {
      name: 'Message: Preview Responsiveness Enabled',
      transform: transforms.message.parcelData,
    },
    preview_mode_updated: {
      name: 'Message: Preview Mode Updated',
      transform: transforms.message.parcelData,
    },
    preview_layout_updated: {
      name: 'Message: Preview Layout Updated',
      transform: transforms.message.parcelData,
    },
    color_scheme_updated: {
      name: 'Message: Color Scheme Updated',
      transform: transforms.message.parcelData,
    },
    visual_impairment_enabled: {
      name: 'Message: Visual Impairment Enabled',
      transform: transforms.message.parcelData,
    },
    image_blocking_enabled: {
      name: 'Message: Image Blocking Enabled',
      transform: transforms.message.parcelData,
    },
    screenshot_captured: {
      name: 'Message: Screenshot Captured',
      transform: transforms.message.parcelData,
    },
    links_validated: {
      name: 'Message: Links Validated',
      transform: transforms.message.parcelData,
    },
    images_validated: {
      name: 'Message: Images Validated',
      transform: transforms.message.parcelData,
    },
    spamassassin_validated: {
      name: 'Message: SpamAssassin Validated',
      transform: transforms.message.parcelData,
    },
    accessibility_validated: {
      name: 'Message: Accessibility Validated',
      transform: transforms.message.parcelData,
    },
    command_palette_displayed: {
      name: 'Message: Command Palette Displayed',
      transform: transforms.message.parcelData,
    },

    // Visual Editor triggered events
    insert_panel_toggled: {
      name: 'Editor: Insert Panel Toggled',
      transform: transforms.message.visualEditorToggledPanelData,
    },
    layers_panel_toggled: {
      name: 'Editor: Layers Panel Toggled',
      transform: transforms.message.visualEditorToggledPanelData,
    },
    code_panel_toggled: {
      name: 'Editor: Code Panel Toggled',
      transform: transforms.message.visualEditorToggledPanelData,
    },
    properties_panel_toggled: {
      name: 'Editor: Properties Panel Toggled',
      transform: transforms.message.visualEditorToggledPanelData,
    },
    component_added: {
      name: 'Editor: Component Added',
      transform: transforms.message.visualEditorEncompassComponentAddedData,
    },
    component_converted: {
      name: 'Editor: Component Converted',
      transform: transforms.message.visualEditorEncompassComponentConvertedData,
    },
  },
  segment: {
    duplicated: {
      name: 'Segment: Duplicated',
      transform: transforms.segment.base,
      debounce(data) {
        return data.model?.isManual;
      },
    },
    list_viewed_searched_or_filtered: {
      name: 'Segment: List Viewed Searched or Filtered',
      transform: transforms.segment.listDetails,
      debounce(data) {
        if (!data?.route) {
          return false;
        }

        // If a search query is present, we debounce this event so that an event
        // isn't sent for every single keystroke.
        return isPresent(data.route.queryParams.search);
      },
    },
    creation_started: {
      name: 'Segment: Creation Started',
      transform: transforms.segment.creationStarted,
    },
    type_chosen: {
      name: 'Segment: Type Chosen',
      transform: transforms.segment.type,
    },
    overview_tab_viewed: {
      name: 'Segment: Overview Tab Viewed',
      transform: transforms.segment.base,
    },
    edit_started: {
      name: 'Segment: Edit Started',
      transform: transforms.segment.editStarted,
    },
    edit_discarded: {
      name: 'Segment: Edit(s) Discarded',
      transform: transforms.segment.base,
    },
    usage_tab_viewed: {
      name: 'Segment: Usage Tab Viewed',
      transform: transforms.segment.base,
    },
    ad_audiences_tab_viewed: {
      name: 'Segment: Ad Audiences Tab Viewed',
      transform: transforms.segment.base,
    },
    segment_membership_timeframe_filter_changed: {
      name: 'Segment: Segment Membership Timeframe Changed',
      transform: transforms.segment.segmentMembershipTimeframeChanged,
    },
    segment_membership_export_timeframe_changed: {
      name: 'Segment: Segment Membership Export Timeframe Changed',
      transform: transforms.segment.segmentMembershipTimeframeChanged,
    },
    people_tab_viewed: {
      name: 'Segment: People Tab Viewed',
      transform: transforms.segment.peopleTabViewed,
    },
    segment_membership_chart_hover: {
      name: 'Segment: Segment Membership Chart Hover',
      transform: transforms.segment.chartHover,
    },
    segment_membership_change_chart_hover: {
      name: 'Segment: Segment Membership Change Chart Hover',
      transform: transforms.segment.chartHover,
    },
    segment_condition_selected: {
      name: 'Segment: Condition Selected',
      transform: transforms.segment.conditionSelected,
    },
    documentation_viewed: {
      name: 'Segment: Documentation Viewed',
      transform: transforms.segment.documentationViewed,
    },
    json_condition_edited: {
      name: 'Segment: JSON Condition Edited',
      transform: transforms.segment.jsonConditionEdited,
    },
    segment_backfill_started: {
      name: 'Segment: Campaign Backfill Started',
      transform: transforms.segment.backfillStarted,
    },
    segment_backfill_completed: {
      name: 'Segment: Campaign Backfill Completed',
      transform: transforms.segment.backfillCompleted,
    },
  },
  people: {
    manual_person_creation_started: {
      name: 'People: Manual Person Creation Started',
    },
    list_viewed_searched_filtered: {
      name: 'People: List Viewed, searched, or filtered',
      transform: transforms.people.listViewedSearchedFiltered,
    },
    person_attributes_viewed: {
      name: 'People: Person Attributes Viewed',
      transform: transforms.people.person,
    },
    person_overview_viewed: {
      name: 'People: Person Overview Viewed',
      transform: transforms.people.person,
    },
    person_segments_viewed: {
      name: 'People: Person Segments Viewed',
      transform: transforms.people.personSegmentsRouteView,
    },
    person_devices_viewed: {
      name: 'People: Person Devices Viewed',
      transform: transforms.people.person,
    },
    person_drafts_viewed: {
      name: 'People: Person Drafts Tab Viewed',
      transform: transforms.people.person,
    },
    person_relationships_viewed: {
      name: 'People: Person Relationships Tab Viewed',
      transform: transforms.people.person,
    },
    person_sent_viewed: {
      name: 'People: Person Sent Tab Viewed',
      transform: transforms.people.person,
    },
    person_activity_viewed: {
      name: 'People: Person Activity Viewed',
      transform: transforms.people.person,
    },
    person_journeys_viewed: {
      name: 'People: Person Journeys Viewed',
      transform: transforms.people.person,
    },
    specific_person_journey_viewed: {
      name: 'People: Specific Person Journey Viewed',
      transform: transforms.people.person,
    },
    id_tooltip_hovered: {
      name: 'People: Person id Tooltip Hovered',
      transform: transforms.people.idTooltipHovered,
    },
    merge_setup_tab_viewed: {
      name: 'Merge: Setup Tab Viewed',
      transform: transforms.people.mergeSetupTabViewed,
    },
    merge_review_and_confirm_tab_viewed: {
      name: 'Merge: Review and Confirm Tab Viewed',
      transform: transforms.people.mergeSummary,
    },
    merge_person_searched: {
      name: 'Merge: Person Searched',
      transform: transforms.people.mergePersonSearched,
    },
    merge_person_selected: {
      name: 'Merge: Person Selected',
      transform: transforms.people.mergeProfileType,
    },
    merge_person_cleared: {
      name: 'Merge: Person Cleared',
      transform: transforms.people.mergeProfileType,
    },
    merge_people_swapped: {
      name: 'Merge: People Swapped',
    },
    merge_attributes_searched: {
      name: 'Merge: Attributes Searched',
      transform: transforms.people.mergedAttributesSearched,
    },
    merge_feedback_request_started: {
      name: 'Merge: Feedback Request Started',
      transform: transforms.people.mergeFeedbackRequestStarted,
    },
  },
  objects: {
    list_viewed_searched_or_filtered: {
      name: 'Object: List Viewed, Searched or Filtered',
      transform: transforms.objects.listViewedSearchedOrFiltered,
    },
    business_type_self_selected: {
      name: 'User: Business Type Self-Selected',
      transform: transforms.objects.businessTypeSelfSelected,
    },
    object_create_started: {
      name: 'Object: Create Started',
    },
    object_displayed_attribute_edit_started: {
      name: 'Object: Displayed Attribute Edit Started',
      transform: transforms.objects.base,
    },
    object_new_attribute_create_clicked: {
      name: 'Object: New Attribute Create Clicked ',
      transform: transforms.objects.base,
    },
    object_overview_tab_viewed: {
      name: 'Object: Overview Tab Viewed',
      transform: transforms.objects.base,
    },
    object_attributes_tab_viewed: {
      name: 'Object: Attributes Tab Viewed',
      transform: transforms.objects.base,
    },
    object_relationship_tab_viewed: {
      name: 'Object: Relationships Tab Viewed',
      transform: transforms.objects.base,
    },
    object_activity_tab_viewed: {
      name: 'Object: Activity Tab Viewed',
      transform: transforms.objects.base,
    },
    object_type_list_viewed: {
      name: 'Object Type: List Viewed',
      transform: transforms.objects.objectTypeListViewed,
    },
    object_type_predefined_list_viewed: {
      name: 'Object Type: Predefined List Viewed',
      transform: transforms.objects.objectTypeListViewed,
    },
    object_type_create_started: {
      name: 'Object Type: Create Started',
    },
    object_type_edit_started: {
      name: 'Object Type: Edit Started',
    },
    object_api_example_copied: {
      name: 'Object Type: Copy API Request Clicked',
    },
    object_guide_page_link_copied: {
      name: 'Object Type: Copy Page Link Clicked',
    },
    relationships_manual_add_started: {
      name: 'Object: Relationship Manual Add Started',
      transform: transforms.objects.relationships,
    },
    relationships_manual_add_completed: {
      name: 'Object: Relationship Manual Add Completed',
      transform: transforms.objects.relationships,
    },
    relationships_manual_delete_completed: {
      name: 'Object: Relationship Manually Deleted',
      transform: transforms.objects.relationships,
    },
  },
  shortcuts: {
    opened: {
      name: 'Shortcuts: Opened',
      transform: transforms.shortcuts.base,
    },
    clicked: {
      name: 'Shortcuts: Clicked',
      transform: transforms.shortcuts.clicked,
    },
    create_started: {
      name: 'Shortcuts: Create Started',
      transform: transforms.shortcuts.base,
    },
    edit_started: {
      name: 'Shortcuts: Edit Started',
      transform: transforms.shortcuts.base,
    },
    delete_started: {
      name: 'Shortcuts: Delete Started',
      transform: transforms.shortcuts.base,
    },
  },
  audience: {
    add_network_started: {
      name: 'Audience: Add Network Started',
      transform: transforms.adNetworks.base,
    },
    sync_creation_started: {
      name: 'Audience: Sync Creation Started',
      transform: transforms.segment.base,
    },
  },
  integration: {
    list_viewed: {
      name: 'Integration: List Viewed',
      transform: transforms.integrations.listViewed,
    },
    list_viewed_public: {
      name: 'Integration: List Viewed',
      transform: transforms.integrations.listViewedPublic,
    },
    segment_in_success_viewed: {
      name: 'Integration: Enabled',
      transform: transforms.integrations.segmentEnabled,
    },
    clicked: {
      name: 'Integration: Clicked',
      transform: transforms.integrations.click,
    },
    api_credentials_creation_started: {
      name: 'Integration: API Credentials Creation Started',
      transform: transforms.integrations.keyType,
    },
    api_credentials_deletion_started: {
      name: 'Integration: API Credentials Deletion Started',
      transform: transforms.integrations.keyType,
    },
    api_credentials_copied: {
      name: 'Integration: API Credentials Copied',
      transform: transforms.integrations.apiCredentialCopied,
    },
    request_started: {
      name: 'Integration: Request Started',
      transform: transforms.integrations.requestStarted,
    },
    request_completed: {
      name: 'Integration: Request Completed',
      transform: transforms.integrations.requestCompleted,
    },
    landing_page_button_clicked: {
      name: 'Integration: Landing Page Button Clicked',
      transform: transforms.integrations.landingPageButtonClicked,
    },
    request_scheduling_started: {
      name: 'Integration: Request Scheduling Started',
      transform: transforms.integrations.requestSchedulingStarted,
    },
    list_searched: {
      name: 'Integration: List Searched or Filtered',
      transform: transforms.integrations.listSearchedOrFiltered,
      debounce() {
        return true;
      },
    },
    list_filtered: {
      name: 'Integration: List Searched or Filtered',
      transform: transforms.integrations.listSearchedOrFiltered,
    },
    notify_when_available_clicked: {
      name: 'Integration: Notify When Available Clicked',
      transform: transforms.integrations.notifyWhenAvailableClicked,
    },
    use_case_link_clicked: {
      name: 'Integration: Use Case Link Clicked',
      transform: transforms.integrations.useCaseLinkClicked,
    },
    tracking_api_keys_viewed: {
      name: 'Integration: Tracking API Keys Viewed',
    },
    app_api_keys_viewed: {
      name: 'Integration: App API Keys Viewed',
    },
    enable_started: {
      name: 'Integration: Enable Started',
      transform: transforms.integrations.enableStarted,
    },
    enabled: {
      name: 'Integration: Enabled',
      transform: transforms.integrations.enabled,
    },
    verify_started: {
      name: 'Integration: Verify Started',
      transform: transforms.integrations.verifyStarted,
    },
    verify_completed: {
      name: 'Integration: Verify Completed',
      transform: transforms.integrations.verifyCompleted,
    },
    disable_started: {
      name: 'Integration: Disable Started',
      transform: transforms.integrations.disableStarted,
    },
    disabled: {
      name: 'Integration: Disabled',
      transform: transforms.integrations.disabled,
    },
    clear_started: {
      name: 'Integration: Clear Connection Started',
      transform: transforms.integrations.clear,
    },
    clear_completed: {
      name: 'Integration: Clear Connection Completed',
      transform: transforms.integrations.clearCompleted,
    },
    reporting_webhook_test_sent: {
      name: 'Integration: Reporting Webhook Test Sent',
      transform: transforms.integrations.reportingWebhookTestSent,
    },
    reporting_webhook_key_copied: {
      name: 'Integration: Reporting Webhook Key Copied',
      transform: transforms.integrations.reportingWebhookKeyCopied,
    },
    edit_started: {
      name: 'Integration: Edit Started',
      transform: transforms.integrations.editStarted,
    },
    error_encountered: {
      name: 'Integration: Error Encountered',
      transform: transforms.integrations.errorEncountered,
    },
    data_campaign_learn_how_clicked: {
      name: 'Integration: Data Campaign Learn How Clicked',
      transform: transforms.integrations.dataCampaignLinkClicked,
    },
    data_campaign_video_clicked: {
      name: 'Integration: Data Campaign Video Clicked',
      transform: transforms.integrations.dataCampaignLinkClicked,
    },
    data_campaign_example_clicked: {
      name: 'Integration: Data Campaign Example Clicked',
      transform: transforms.integrations.dataCampaignLinkClicked,
    },
  },
  email: {
    sending_domains_tab_viewed: {
      name: 'Email: Sending Domains Tab Viewed',
    },
    deliverability_domain_viewed: {
      name: 'Email: Deliverability Domain Viewed',
    },
    deliverability_domain_checking_started: {
      name: 'Email: Deliverability Domain Checking Started',
      transform: transforms.message.domainVerificationStarted,
    },
    link_tracking_domain_viewed: {
      name: 'Email: Link Tracking Domain Viewed',
      transform: transforms.message.linkTracking,
    },
    link_tracking_domain_checking_started: {
      name: 'Email: Link Tracking Domain Checking Started',
      transform: transforms.message.linkTracking,
    },
    mail_servers_domain_viewed: {
      name: 'Email: Mail Servers Domain Viewed',
    },
    change_layout_clicked: {
      name: 'Email: Change Layout Clicked',
      transform: transforms.message.messageMultiLanguageData,
    },
    change_layout_applied: {
      name: 'Email: Change Layout Applied',
      transform: transforms.message.messageMultiLanguageData,
    },
    preview_plaintext: {
      name: 'Email: Preview Plaintext',
      transform: transforms.message.messageMultiLanguageData,
    },
    smtp_tab_viewed: {
      name: 'Email: Custom SMTP Settings Tab Viewed',
    },
    suppressions_tab_viewed: {
      name: 'Email: Suppressions List Tab Viewed',
    },
    builder_chosen: {
      name: 'Email: Builder Chosen',
      transform: transforms.email.builderChosen,
    },
    preview_template_clicked: {
      name: 'Email: Template Preview Clicked',
      transform: transforms.email.previewTemplateClicked,
    },
    search_performed: {
      name: 'Email: Search Performed',
      transform: transforms.email.searchPerformed,
    },
    custom_smtp_add_server_started: {
      name: 'Custom SMTP Add Server Started',
    },
    custom_smtp_add_server_setup_action_complete: {
      name: 'Custom SMTP Add Server Setup Action Complete',
      transform: transforms.email.customSmtpAddServerSetupActionComplete,
    },
    custom_smtp_add_server_setup_canceled: {
      name: 'Custom SMTP Add Server Setup Canceled',
      transform: transforms.email.customSmtpAddServerSetupCanceled,
    },
    saved_row_started: {
      name: 'Saved Row Started',
      transform: transforms.email.savedRowEvent,
    },
    saved_row_canceled: {
      name: 'Saved Row Canceled',
      transform: transforms.email.savedRowEvent,
    },
    saved_row_inserted: {
      name: 'Saved Row Inserted',
      transform: transforms.email.savedRowEvent,
    },
    html_toggled: {
      name: 'Email: HTML Toggled',
      transform: transforms.email.htmlToggled,
    },
  },
  slack: {
    settings_viewed: {
      name: 'Slack: Message Settings Viewed',
      transform: transforms.integrations.slackSettingsView,
    },
    enabling_started: {
      name: 'Slack: Enabling Started',
      transform: transforms.integrations.eventWithSource,
    },
  },
  twilio: {
    settings_viewed: {
      name: 'Twilio: Message Settings Viewed',
      transform: transforms.integrations.twilioSettingsView,
    },
  },
  push: {
    message_settings_viewed: {
      name: 'Push: Message Settings Viewed',
      transform: transforms.push.messageSettingsViewed,
    },
    configuration_viewed: {
      name: 'Push: Configuration Viewed',
      transform: transforms.push.configurationViewed,
    },
    setup_started: {
      name: 'Push: Setup Started',
      transform: transforms.push.setupView,
    },
    setup_checklist_viewed: {
      name: 'Push: Setup Checklist Viewed',
      transform: transforms.push.setupChecklistView,
    },
    authorization_added: {
      name: 'Push: Authorization Added',
      transform: transforms.push.autorizationAdded,
    },
    authorization_updated: {
      name: 'Push: Authorization Updated',
      transform: transforms.push.autorizationUpdated,
    },
    push_disabled: {
      name: 'Push: Push Disabled',
      transform: transforms.push.pushDisabled,
    },
  },
  in_app: {
    settings_viewed: {
      name: 'In-App Message: Settings Viewed',
      transform: transforms.integrations.gistSettingsView,
    },
    enabling_started: {
      name: 'In-App Message: Enabling Started',
      transform: transforms.integrations.gistSettingsView,
    },
    enabling_completed: {
      name: 'In-App Message: Enabling Completed',
      transform: transforms.integrations.gistSettingsView,
    },
    disabled: {
      name: 'In-App Message: Disabled',
      transform: transforms.integrations.gistSettingsView,
    },
    edit_completed: {
      name: 'In-App: Edit Completed',
      transform: transforms.inApp.editCompleted,
    },
    edit_message_clicked: {
      name: 'In-App: Edit Message Clicked',
    },
    builder_chosen: {
      name: 'In-App: Builder Chosen',
      transform: transforms.inApp.builderChosen,
    },
  },
  dashboard: {
    viewed: {
      name: 'Dashboard: Viewed',
    },
    navigate_away: {
      name: 'Dashboard: Navigate Away',
      transform: transforms.dashboard.navigateAway,
    },
    chart_changed: {
      name: 'Dashboard: Chart Changed',
      transform: transforms.dashboard.chartChanged,
    },
    chart_hover: {
      name: 'Dashboard: Chart Hover',
      transform: transforms.dashboard.chartHover,
    },
    timeframe_filters_changed: {
      name: 'Dashboard: Timeframe Filters Changed',
      transform: transforms.dashboard.timeframeFiltersChanged,
    },
    leaderboard_card_display_changed: {
      name: 'Dashboard: Leaderboard Card Display Changed',
      transform: transforms.dashboard.leaderboardCardDisplayChanged,
    },
    tests_card_display_changed: {
      name: 'Dashboard: Tests Card Display Changed',
      transform: transforms.dashboard.testsCardDisplayChanged,
    },
    links_channel_changed: {
      name: 'Dashboard: Links Channel Changed',
      transform: transforms.dashboard.channel,
    },
  },
  export: {
    list_viewed: {
      name: 'Export: List Viewed',
    },
  },
  logs: {
    viewed: {
      name: 'Log: Activity Log Viewed',
      transform: transforms.logs.activityLogView,
    },
    deliveries_or_drafts_viewed: {
      name: 'Log: Deliveries and Drafts Log Viewed',
      transform: transforms.logs.deliveriesOrDraftsView,
    },
    data_index_viewed: {
      name: 'Log: Data Index Viewed',
      transform: transforms.logs.dataIndexView,
    },
    data_index_item_viewed: {
      name({ route }) {
        let parent = route!.find!((route: RouteInfo) => route.name === 'data-index.type');

        if (parent!.params.type === 'attributes') {
          return 'Log: Data Index Attribute Viewed';
        } else {
          return 'Log: Data Index Event Viewed';
        }
      },
    },
    specific_activity_viewed: {
      name: 'Log: Activity Log Specific Activity Viewed',
      transform: transforms.logs.specificActivityView,
    },
    more_logs_loaded: {
      name: 'Log: More Logs Loaded',
      transform: transforms.logs.moreLogsLoaded,
    },
  },
  workspace: {
    compare_plans_from_workspaces_viewed: {
      name: 'Workspace: Compare Plans From Workspaces Viewed',
    },
    creation_started: {
      name: 'Workspace: Creation Started',
      transform: transforms.workspace.createStarted,
    },
    settings_viewed: {
      name: 'Workspace: Settings Viewed',
    },
    snippets_viewed: {
      name: 'Workspace: Snippets Viewed',
    },
    general_settings_viewed: {
      name: 'Workspace: General Settings Viewed',
      transform: transforms.workspace.routeView,
    },
    tracking_api_keys_viewed: {
      name: 'Workspace: Tracking API Keys Viewed',
    },
    app_api_keys_viewed: {
      name: 'Workspace: App API Keys Viewed',
    },
    api_credentials_creation_started: {
      name: 'Workspace: API Credentials Creation Started',
      transform: transforms.integrations.keyType,
    },
    api_credentials_deletion_started: {
      name: 'Workspace: API Credentials Deletion Started',
      transform: transforms.integrations.keyType,
    },
    api_credentials_copied: {
      name: 'Workspace: API Credentials Copied',
      transform: transforms.integrations.apiCredentialCopied,
    },
    switched: {
      name: 'Workspace: Switched to Different Workspace',
    },
    language_settings_viewed: {
      name: 'Workspace: Language Settings Viewed',
    },
    message_limits_viewed: {
      name: 'Workspace: Message Limits Viewed',
      transform: transforms.workspace.routeView,
    },
    message_limits_edit_started: {
      name: 'Workspace: Message Limits Edit Started',
      transform: transforms.workspace.messageLimitsEditStarted,
    },
    identifier_clicked: {
      name: 'Workspace: Identifier Clicked',
      transform: transforms.workspace.identifierClicked,
    },
    identifier_change_started: {
      name: 'Workspace: Identifier Change Started',
      transform: transforms.workspace.identifierChangeStarted,
    },
    identifier_change_submitted: {
      name: 'Workspace: Identifier Change Submitted',
      transform: transforms.workspace.identifierChangeSubmitted,
    },
    anonymous_events_merge_enabled: {
      name: 'Workspace: Anonymous Event Merge Enabled',
    },
    anonymous_events_merge_disabled: {
      name: 'Workspace: Anonymous Event Merge Disabled',
    },
    auto_identify_track_links_enabled: {
      name: 'Workspace: Auto-identify Tracked Links Enabled',
    },
    auto_identify_track_links_disabled: {
      name: 'Workspace: Auto-identify Tracked Links Disabled',
    },
    open_tracking_disabled: {
      name: 'Workspace: Open Tracking Disabled',
    },
    open_tracking_enabled: {
      name: 'Workspace: Open Tracking Enabled',
    },
    auto_merge_enabled: {
      name: 'Workspace: Multi-identifier Merge Enabled',
    },
    auto_merge_disabled: {
      name: 'Workspace: Multi-identifier Merge Disabled',
    },
    update_identifiers_by_id_changed: {
      name: 'Workspace: Update Identifiers By ID Changed',
      transform: transforms.workspace.updateIdentifiersById,
    },
    delivery_settings_updated: {
      name: 'Workspace: Delivery Settings Updated',
      transform: transforms.workspace.updateDeliverySettings,
    },
    delivery_email_updated: {
      name: 'Workspace: Delivery Email Updated',
    },
  },
  snippets: {
    creation_started: {
      name: 'Snippet: Creation Started',
    },
    edit_started: {
      name: 'Snippet: Edit Started',
      transform: transforms.snippets.base,
    },
    delete_started: {
      name: 'Snippet: Delete Started',
      transform: transforms.snippets.base,
    },
    list_sorted: {
      name: 'Snippet: List Sorted',
      transform: transforms.snippets.listSorted,
    },
    detail_page_viewed: {
      name: 'Snippet: Detail Page Viewed',
      transform: transforms.snippets.detailPageViewed,
    },
    list_searched: {
      name: 'Snippet: List Searched',
      transform: transforms.snippets.listSearched,
    },
    list_filtered: {
      name: 'Snippet: List Filtered',
      transform: transforms.snippets.listFiltered,
    },
  },
  collections: {
    creation_started: {
      name: 'Collections: Creation Started',
    },
    delete_started: {
      name: 'Collections: Delete Started',
      transform: transforms.collections.base,
    },
    list_viewed: {
      name: 'Collections: List Viewed',
    },
    update_started: {
      name: 'Collections: Update Started',
      transform: transforms.collections.base,
    },
  },
  analysis: {
    viewed: {
      name: 'Analysis: Viewed',
    },
    report_criteria_changed: {
      name: 'Analysis: Report Criteria Changed',
      transform: transforms.analysis.reportCriteriaChanged,
    },
    report_started: {
      name: 'Analysis: Report Started',
      transform: transforms.analysis.reportStarted,
    },
    report_completed: {
      name: 'Analysis: Report Completed',
      transform: transforms.analysis.reportCompleted,
    },
    refine_results_started: {
      name: 'Analysis: Refine Results Started',
      transform: transforms.analysis.refineResultsStarted,
    },
    refine_results_completed: {
      name: 'Analysis: Refine Results Completed',
      transform: transforms.analysis.refineResultsCompleted,
    },
    display_option_changed: {
      name: 'Analysis: Display Option Changed',
      transform: transforms.analysis.displayOptionChanged,
    },
    chart_hover: {
      name: 'Analysis: Chart Hover',
      transform: transforms.analysis.chartHover,
    },
  },
  tags: {
    create_started: {
      name: 'Tags: Create Started',
    },
    create_canceled: {
      name: 'Tags: Create Canceled',
    },
    delete_started: {
      name: 'Tags: Delete Started',
      transform: transforms.tags.base,
    },
    edit_started: {
      name: 'Tags: Edit Started',
      transform: transforms.tags.base,
    },
    manage_started: {
      name: 'Tags: Manage Started',
      transform: transforms.tags.indexTotals,
    },
    manage_completed: {
      name: 'Tags: Manage Completed',
      transform: transforms.tags.indexTotals,
    },
  },
  comments: {
    comment_added: {
      name: 'Comment: Added',
      transform: transforms.comments.base,
    },
    comment_edited: {
      name: 'Comment: Edited',
      transform: transforms.comments.edited,
    },
    comment_deleted: {
      name: 'Comment: Deleted',
      transform: transforms.comments.withoutContent,
    },
    comment_replied: {
      name: 'Comment: Replied',
      transform: transforms.comments.replied,
    },
    comment_resolved: {
      name: 'Comment: Resolved',
      transform: transforms.comments.withoutContent,
    },
    comment_reopened: {
      name: 'Comment: Re-Opened',
      transform: transforms.comments.withoutContent,
    },
  },
  transactional: {
    join_beta_viewed: {
      name: 'Transactional: Join the Beta Page Viewed',
    },
    index_viewed: {
      name({ model }) {
        if (model?.filter((tm: TransactionalMessageModel) => !tm.isDefault).length === 0) {
          return 'Transactional: Welcome Page Viewed';
        } else {
          return 'Transactional: List Viewed, Searched, or Filtered';
        }
      },
      transform: transforms.transactional.listView,
      debounce: (data) => isPresent(data?.route?.queryParams.search),
    },
    overview_tab_viewed: {
      name: 'Transactional: Overview Tab Viewed',
      transform: transforms.transactional.overviewTabViewed,
    },
    metrics_tab_viewed: {
      name: 'Transactional: Metrics Tab Viewed',
      transform: transforms.transactional.overviewTabViewed,
    },
    drafts_tab_viewed: {
      name: 'Transactional: Drafts Tab Viewed',
      transform: transforms.transactional.overviewTabViewed,
    },
    sent_tab_viewed: {
      name: 'Transactional: Sent Tab Viewed',
      transform: transforms.transactional.overviewSentTabViewed,
    },
    create_started: {
      name: 'Transactional: Create Started',
    },
    edit_started: {
      name: 'Transactional: Edit Started',
      transform: transforms.transactional.editStarted,
    },
    add_content_step_viewed: {
      name: 'Transactional: Add Content Step Viewed',
      transform: transforms.transactional.setupStepViewed,
    },
    send_message_step_viewed: {
      name: 'Transactional: Send Message Step Viewed',
      transform: transforms.transactional.setupStepViewed,
    },
    configure_settings_step_viewed: {
      name: 'Transactional: Configure Settings Step Viewed',
      transform: transforms.transactional.setupStepViewed,
    },
    code_copied: {
      name: 'Transactional: Code Copied',
      transform: transforms.transactional.codeCopied,
    },
    activated: {
      name: 'Transactional: Activated',
      transform: transforms.transactional.activated,
    },
    transactional_composer_viewed: {
      name({ model }) {
        if (model?.message.hasTemplateContent) {
          return `${capitalize(model.message.type)}: Edit Started`;
        } else {
          return 'Transactional: Add Content Started';
        }
      },
      transform({ model }) {
        if (model?.message.hasTemplateContent) {
          return transforms.transactional.editContentStarted({ model });
        } else {
          return transforms.transactional.addContentStarted({ model });
        }
      },
    },
    metrics_display_option_changed: {
      name: 'Transactional: Metrics Display Option Changed',
      transform: transforms.transactional.metricsDisplayOptionChanged,
    },
    timeframe_filters_changed: {
      name: 'Transactional: Timeframe Filters Changed',
      transform: transforms.transactional.timeframeFiltersChanged,
    },
    delete_started: {
      name: 'Transactional: Delete Started',
      transform: transforms.transactional.deleteStarted,
    },
    copy_started: {
      name: 'Transactional: Copy Started',
      transform: transforms.transactional.messageCopyStarted,
    },
    copy_to_workspace_started: {
      name: 'Transactional: Copy To Workspace Started',
      transform: transforms.transactional.messageCopyStarted,
    },
    hello_world_message_create_started: {
      name: 'Transactional: "Hello World" Message Create Started',
    },
  },
  events: {
    send_started: {
      name: 'Event: Send Started',
      transform: transforms.events.sendStarted,
    },
    send_completed: {
      name: 'Event: Send Completed',
      transform: transforms.events.sendCompleted,
    },
    error_encountered: {
      name: 'Event: Error Encountered',
      transform: transforms.events.errorEncountered,
    },
  },
  forms: {
    list_viewed_or_filtered: {
      name: 'Forms: List Viewed or Filtered',
      transform: transforms.forms.listViewedOrFiltered,
    },
    settings_viewed: {
      name: 'Forms: Settings Viewed',
    },
    manage_form_started: {
      name: 'Forms: Manage Form Started',
      transform: transforms.forms.formAction,
    },
    feedback_request_started: {
      name: 'Forms: Feedback Request Started',
    },
    form_builders_viewed: {
      name: 'Forms: Form Builders Viewed',
    },
    form_builder_clicked: {
      name: 'Forms: Form Builder Clicked',
      transform: transforms.forms.formBuilderClicked,
    },
    setup_find_form_tab_viewed: {
      name: 'Forms: Find Form Tab Viewed',
      transform: transforms.forms.setupFindFormTabViewed,
    },
    setup_select_form_tab_viewed: {
      name: 'Forms: Select Form Tab Viewed',
      transform: transforms.forms.setupSelectFormTabViewed,
    },
    setup_map_form_fields_tab_viewed: {
      name: 'Forms: Map Form Fields Tab Viewed',
      transform: transforms.forms.source,
    },
    custom_form_code_viewed: {
      name: 'Forms: Code Viewed',
      transform: transforms.forms.codeViewed,
    },
    custom_form_code_copied: {
      name: 'Forms: Code Copied',
      transform: transforms.forms.codeCopied,
    },
  },
  facebook_lead_ads: {
    connect_account_started: {
      name: 'FB Lead Ads: Connect Account Started',
      transform: transforms.facebookLeadAds.source,
    },
    connect_account_completed: {
      name: 'FB Lead Ads: Connect Account Completed',
    },
    connect_business_page_started: {
      name: 'FB Lead Ads: Connect Business Page Started',
    },
    disconnect_business_page_started: {
      name: 'FB Lead Ads: Disconnect Business Page Started',
    },
    conversion_enabled: {
      name: 'FB Lead Ads: Conversion Enabled',
      transform: transforms.facebookLeadAds.source,
    },
  },
  warehouse: {
    settings_viewed: {
      name({ model }) {
        if (model?.sync) {
          return 'Data Warehouse: Settings Viewed';
        } else {
          return 'Data Warehouse: Empty State Viewed';
        }
      },
      transform: transforms.dataWarehouse.withType,
    },
    destination_viewed: {
      name: 'Data Warehouse: Destination Viewed',
      transform: transforms.dataWarehouse.destinationViewed,
    },
    create_sync_started: {
      name: 'Data Warehouse: Create Sync Started',
      transform: transforms.dataWarehouse.withType,
    },
    create_sync_completed: {
      name: 'Data Warehouse: Create Sync Completed',
      transform: transforms.dataWarehouse.withType,
    },
    export_enabled: {
      name: 'Data Warehouse: Export Enabled',
      transform: transforms.dataWarehouse.exportToggled,
    },
    export_disabled: {
      name: 'Data Warehouse: Export Disabled',
      transform: transforms.dataWarehouse.exportToggled,
    },
    disconnect_sync_started: {
      name: 'Data Warehouse: Disconnect Sync Started',
      transform: transforms.dataWarehouse.withType,
    },
    disconnect_sync_completed: {
      name: 'Data Warehouse: Disconnect Sync Completed',
      transform: transforms.dataWarehouse.withType,
    },
    update_configuration_started: {
      name: 'Data Warehouse: Update Configuration Started',
      transform: transforms.dataWarehouse.withType,
    },
    update_configuration_completed: {
      name: 'Data Warehouse: Update Configuration Completed',
      transform: transforms.dataWarehouse.withType,
    },
  },
  sql_imports: {
    create_started: {
      name: 'SQL Sync: Create Started',
      transform(data) {
        return transforms.sqlImports.type({ ...data, hideSyncTarget: true });
      },
    },
    select_database_started: {
      name: 'SQL Sync: Select Database Started',
      transform: transforms.sqlImports.selectDatabaseStarted,
    },
    sync_settings_started: {
      name: 'SQL Sync: Settings Started',
      transform(data) {
        return transforms.sqlImports.type({ ...data, hideSyncTarget: true });
      },
    },
    add_database_started: {
      name: 'SQL Sync: Add Database Started',
      transform: transforms.sqlImports.addDatabaseStarted,
    },
    add_database_failed: {
      name: 'SQL Sync: Add Database Failed',
      transform: transforms.sqlImports.addDatabaseFailed,
    },
    delete_database_started: {
      name: 'SQL Sync: Delete Database Started',
      transform: transforms.sqlImports.deleteDatabaseStarted,
    },
    edit_database_started: {
      name: 'SQL Sync: Edit Database Started',
      transform: transforms.sqlImports.editDatabaseStarted,
    },
    edit_database_failed: {
      name: 'SQL Sync: Edit Database Failed',
      transform: transforms.sqlImports.editDatabaseFailed,
    },
    write_query_started: {
      name: 'SQL Sync: Write Query Started',
      transform: transforms.sqlImports.type,
    },
    write_relationships_query_started: {
      name: 'SQL Sync: Write Relationships Query Started',
      transform: transforms.sqlImports.type,
    },
    skip_relationships_query: {
      name: 'SQL Sync: Skip Relationships Query',
      transform: transforms.sqlImports.type,
    },
    run_query_started: {
      name: 'SQL Sync: Run Query Started',
      transform: transforms.sqlImports.runQueryStarted,
    },
    run_query_failed: {
      name: 'SQL Sync: Run Query Failed',
      transform: transforms.sqlImports.runQueryFailed,
    },
    sync_activate_started: {
      name: 'SQL Sync: Activate Started',
      transform: transforms.sqlImports.type,
    },
    sync_delete_started: {
      name: 'SQL Sync: Delete Started',
      transform: transforms.sqlImports.type,
    },
    sync_pause_started: {
      name: 'SQL Sync: Pause Started',
      transform: transforms.sqlImports.type,
    },
    imports_tab_viewed: {
      name: 'SQL Sync: Sync Imports Viewed',
      transform: transforms.sqlImports.typeAndStatus,
    },
    query_tab_viewed: {
      name: 'SQL Sync: Query Tab Viewed',
      transform: transforms.sqlImports.type,
    },
    relationship_query_tab_viewed: {
      name: 'SQL Sync: Relationship Query Tab Viewed',
      transform: transforms.sqlImports.type,
    },
    preview_timeout_dismiss: {
      name: 'SQL Sync: Preview Timeout Dismiss',
      transform: transforms.sqlImports.type,
    },
    preview_timeout_continue: {
      name: 'SQL Sync: Preview Timeout Continue',
      transform: transforms.sqlImports.type,
    },
    settings_tab_viewed: {
      name: 'SQL Sync: Settings Tab Viewed',
      transform: transforms.sqlImports.type,
    },
    review_import: {
      name: 'SQL Sync: Review Import',
      transform: transforms.sqlImports.reviewImport,
    },
    create_sync_started: {
      name: 'SQL Sync: Create Sync Started',
      transform: transforms.sqlImports.type,
    },
    cancel_import: {
      name: 'SQL Sync: Stop Import',
      transform: transforms.sqlImports.type,
    },
    cancel_import_started: {
      name: 'SQL Sync: Stop Import Started',
      transform: transforms.sqlImports.type,
    },
    cancel_import_canceled: {
      name: 'SQL Sync: Stop Import Canceled',
      transform: transforms.sqlImports.type,
    },
    download_error_file: {
      name: 'SQL Sync: Download Error File',
      transform: transforms.sqlImports.type,
    },
  },
  library: {
    page_viewed: {
      name: 'Library: Page Viewed, Sorted, or Searched',
      transform: transforms.library.messagesQueried,
    },
    message_selected: {
      name: 'Library: Message Selected',
    },
    message_parent_selected: {
      name: 'Library: Message Parent Selected',
    },
  },
  datacenter: {
    learn_more_link_click: {
      name: 'Data Center: Learn More Link Clicked',
      transform: transforms.datacenter.learnMoreLinkClick,
    },
  },
  languages: {
    add_language_canceled: {
      name: 'Languages: Add Language Canceled',
      transform: transforms.languages.translationData,
    },
    add_language_clicked: {
      name: 'Languages: Add Language Clicked',
      transform: transforms.languages.translationDataWithLanguageAttribute,
    },
    delete_language_clicked: {
      name: 'Languages: Delete Language Clicked',
      transform: transforms.languages.deleteLanguageClicked,
    },
    attribute_setup_canceled: {
      name: 'Languages: Attribute Set Up Canceled',
      transform: transforms.languages.baseData,
    },
    different_format_clicked: {
      name: 'Languages: Different Format Clicked',
      transform: transforms.languages.baseData,
    },
    feedback_canceled: {
      name: 'Languages: Feedback Canceled',
      transform: transforms.languages.baseData,
    },
    attribute_info_icon_hovered: {
      name: 'Languages: Language Attribute Info Icon Hovered',
      transform: transforms.languages.translationData,
    },
    settings_link_clicked: {
      name: 'Languages: Language Settings Link Clicked',
      transform: transforms.languages.translationDataWithLanguageAttribute,
    },
    settings_tip_link_clicked: {
      name: 'Languages: Language Settings Tips and Resources Link Clicked',
      transform: transforms.languages.tipLinkClicked,
    },
    language_tab_clicked: {
      name: 'Languages: Language Tab Clicked',
      transform: transforms.languages.tabClicked,
    },
    more_dropdown_clicked: {
      name: 'Languages: More Dropdown Clicked',
      transform: transforms.languages.translationData,
    },
  },
  experimental_features: {
    viewed: {
      name: 'Experimental Features: Page Viewed',
    },
    toggled: {
      name: 'Experimental Features: Feature Toggled',
      transform: transforms.experimentalFeatures.featureToggled,
    },
  },
  asset_library: {
    viewed: {
      name: 'Assets: Asset Library Viewed',
      transform: transforms.assetLibrary.counts,
    },
    file_previewed: {
      name: 'Assets: File Previewed',
      transform: transforms.assetLibrary.fileData,
    },
    file_url_copied: {
      name: 'Assets: File URL Copied',
      transform: transforms.assetLibrary.fileData,
    },
    file_delete_started: {
      name: 'Assets: Asset Delete Started',
      transform: transforms.assetLibrary.fileData,
    },
    folder_add_canceled: {
      name: 'Assets: Folder Add Canceled',
      transform: transforms.assetLibrary.parentFolderData,
    },
    folder_add_started: {
      name: 'Assets: Folder Add Started',
      transform: transforms.assetLibrary.parentFolderData,
    },
    folder_viewed: {
      name: 'Assets: Folder Viewed',
      transform: transforms.assetLibrary.folderViewedData,
    },
    folder_delete_started: {
      name: 'Assets: Folder Delete Started',
      transform: transforms.assetLibrary.folderDeleteData,
    },
    bulk_delete_started: {
      name: 'Assets: Bulk Delete Started',
      transform: transforms.assetLibrary.bulkDeleteData,
    },
    upload_image_started: {
      name: 'Assets: Upload Image Started',
      transform: transforms.assetLibrary.fileUploadData,
    },
  },
  subscription_center: {
    topics_viewed: {
      name: 'Subscription Center: Topics Page Viewed',
    },
    branding_page_viewed: {
      name: 'Subscription Center: Branding Page Viewed',
    },
    add_topic_started: {
      name: 'Subscription Center: Add Topic Started',
    },
    delete_topic_started: {
      name: 'Subscription Center: Delete Topic Started',
    },
    edit_topic_started: {
      name: 'Subscription Center: Edit Topic Started',
    },
    preview_clicked: {
      name: 'Subscription Center: Preview Page Clicked',
    },
    preview_draft_clicked: {
      name: 'Subscription Center: Preview Draft Page Clicked',
    },
    example_clicked: {
      name: 'Subscription Center: Example Page Clicked',
    },
    activation_completed: {
      name: 'Subscription Center: Activation Completed',
    },
    deactivation_completed: {
      name: 'Subscription Center: Deactivation Completed',
    },
    learn_more_clicked: {
      name: 'Subscription Center: Learn More Clicked',
    },
    get_started_clicked: {
      name: 'Subscription Center: Get Started Clicked',
    },
    settings_page_viewed: {
      name: 'Subscription Center: Settings Page Viewed',
    },
    branding_updated: {
      name: 'Subscription Center: Branding Updated',
    },
    branding_exited: {
      name: 'Subscription Center: Branding Exited Without Changes',
    },
    add_languages_started: {
      name: 'Subscription Center: Add Languages Started',
    },
    edit_languages_started: {
      name: 'Subscription Center: Edit Languages Started',
    },
    edit_languages_submitted: {
      name: 'Subscription Center: Edit languages submitted',
      transform: transforms.subscriptionCenter.editLanguagesSubmitted,
    },
    language_edit_started: {
      name: 'Subscription Center: Language edit started',
    },
    language_edited: {
      name: 'Subscription Center: Language edited',
    },
    language_deleted: {
      name: 'Subscription Center: Language deleted',
    },
    language_status_changed: {
      name: 'Subscription Center: Language status changed',
      transform: transforms.subscriptionCenter.languageStatusChanged,
    },
    set_bulk_preferences_started: {
      name: 'Subscription Center: Set Bulk Preferences Started',
    },
    set_bulk_preferences_submitted: {
      name: 'Subscription Center: Set Bulk Preferences Submitted',
    },
    set_bulk_preferences_filtered: {
      name: 'Subscription Center: Set Bulk Preferences Filtered',
      transform: transforms.subscriptionCenter.setBulkPreferencesFiltered,
    },
  },
  plan_billing: {
    switch_to_premium_button_clicked: {
      name: 'Plan and Billing: Switch to Premium Button Clicked',
      transform: transforms.planBilling.switchToPremiumClicked,
    },
    essentials_plan_post_checkout_viewed: {
      name: 'Plan and Billing: Essentials Plan Post Checkout Viewed',
      transform: transforms.planBilling.essentialsPostCheckoutViewed,
    },
    premium_plan_post_checkout_viewed: {
      name: 'Plan and Billing: Premium Plan Post Checkout Viewed',
      transform: transforms.planBilling.premiumPostCheckoutViewed,
    },
    included_in_plan_viewed: {
      name: "Plan and Billing: What's included in my plan viewed",
      transform: transforms.planBilling.includedInPlanViewed,
    },
    view_all_features_clicked: {
      name: 'Plan and Billing: View all features clicked',
      transform: transforms.planBilling.viewAllFeaturesClicked,
    },
    sales_or_contracts_lets_talk_clicked: {
      name: 'Plan and Billing: Sales or Contracts Lets Talk Clicked',
      transform: transforms.planBilling.startSalesOrContractsLetsTalkClicked,
    },
    buy_or_request_access_clicked: {
      name: 'Plan and Billing: Buy or Request Access Clicked',
      transform: transforms.planBilling.buyOrRequestAccessClicked,
    },
    plan_action_clicked: {
      name: 'Plan and Billing: Plan Action Clicked',
      transform: transforms.planBilling.planActionClicked,
    },
    your_plan_available_plan_cta_clicked: {
      name: 'Plan and Billing: Your Plan Available Plan CTA Clicked',
      transform: transforms.planBilling.yourPlanAvailablePlanCtaClicked,
    },
    limits_banner_learn_more_clicked: {
      name: 'Plan and Billing: Limits Banner Learn More Clicked',
      transform: transforms.planBilling.limitsBannerLearnMoreClicked,
    },
    start_journeys_trial_clicked: {
      name: 'Plan and Billing: Start Journeys Trial Clicked',
      transform: transforms.planBilling.startJourneysTrialClicked,
    },
    view_billing_help: {
      name: 'Plan and Billing: View Billing Help',
      transform: transforms.planBilling.viewBillingHelp,
    },
    available_plans_section_changed: {
      name: 'Plan and Billing: Available Plans Section Changed',
      transform: transforms.planBilling.availablePlansSectionChanged,
    },
    start_cdp_starter_subscription_clicked: {
      name: 'Plan and Billing: Start CDP Starter Subscription Clicked - Journeys Trial Canceled',
      transform: transforms.planBilling.startCdpStarterSubscriptionClicked,
    },
  },
  help_center: {
    menu_item_clicked: {
      name({ data }) {
        return `Help Center: ${data.button_clicked}`;
      },
      transform: transforms.helpCenter.userData,
    },
    docs_search_input: {
      name: 'Help Center: Docs Search Input Interacted',
      transform: transforms.helpCenter.userData,
    },
    docs_searched_duration: {
      name: 'Help Center: Docs Search Performance',
      transform: transforms.helpCenter.docsSearched,
    },
    docs_search_results_clicked: {
      name: 'Help Center: Docs Search Results Clicked',
      transform: transforms.helpCenter.docsSearchResultsClicked,
    },
    contextual_help_item_clicked: {
      name: 'Help Center: Learn and Discover Content Clicked',
      transform: transforms.helpCenter.contextualHelpitemClicked,
    },
    ai_powered_search_banner_clicked: {
      name: 'Help Center: AI Powered Search Banner Clicked',
      transform: transforms.helpCenter.userData,
    },
  },
  inline_support: {
    inline_support_opened: {
      name: 'Inline Support: Opened',
      transform: transforms.inlineSupport.userData,
    },
    inline_support_closed: {
      name: 'Inline Support: Closed',
      transform: transforms.inlineSupport.userData,
    },
    inline_support_next_page_clicked: {
      name: 'Inline Support: Next Page Clicked',
      transform: transforms.inlineSupport.userData,
    },
    inline_support_previous_page_clicked: {
      name: 'Inline Support: Previous Page Clicked',
      transform: transforms.inlineSupport.userData,
    },
    inline_support_cancel_clicked: {
      name: 'Inline Support: Cancel Clicked',
      transform: transforms.inlineSupport.userData,
    },
    inline_support_cancel_dismissed: {
      name: 'Inline Support: Cancel Dismissed',
      transform: transforms.inlineSupport.userData,
    },
    inline_support_cancel_confirmed: {
      name: 'Inline Support: Cancel Confirmed',
      transform: transforms.inlineSupport.userData,
    },
    inline_support_page_viewed: {
      name: 'Inline Support: Page Viewed',
      transform: transforms.inlineSupport.pageViewed,
    },
    inline_support_docs_suggestion_clicked: {
      name: 'Inline Support: Docs Suggestion Clicked',
      transform: transforms.inlineSupport.docsSuggestionClicked,
    },
    inline_support_field_input: {
      name: 'Inline Support: Field Input',
      transform: transforms.inlineSupport.fieldInput,
    },
    inline_support_support_access_granted: {
      name: 'Inline Support: Support Access Granted',
      transform: transforms.inlineSupport.supportAccessGranted,
    },
    inline_support_support_access_error: {
      name: 'Inline Support: Support Access Error',
      transform: transforms.inlineSupport.userData,
    },
    inline_support_submit_clicked: {
      name: 'Inline Support: Submit Clicked',
      transform: transforms.inlineSupport.userData,
    },
    inline_support_submit_error: {
      name: 'Inline Support: Submit Error',
      transform: transforms.inlineSupport.userData,
    },
    inline_support_submit_success: {
      name: 'Inline Support: Submit Success',
      transform: transforms.inlineSupport.userData,
    },
  },
  inline_contracts_sales: {
    inline_contracts_sales_form_opened: {
      name: 'Inline Contracts Sales Form: Opened',
      transform: transforms.inlineContractsSales.userData,
    },
    inline_contracts_sales_form_closed: {
      name: 'Inline Contracts Sales Form: Closed',
      transform: transforms.inlineContractsSales.userData,
    },
    inline_contracts_sales_form_cancel_clicked: {
      name: 'Inline Contracts Sales Form: Cancel Clicked',
      transform: transforms.inlineContractsSales.userData,
    },
    inline_contracts_sales_form_cancel_dismissed: {
      name: 'Inline Contracts Sales Form: Cancel Dismissed',
      transform: transforms.inlineContractsSales.userData,
    },
    inline_contracts_sales_form_field_input: {
      name: 'Inline Contracts Sales Form: Field Input',
      transform: transforms.inlineContractsSales.fieldInput,
    },
    inline_contracts_sales_form_submit_clicked: {
      name: 'Inline Contracts Sales Form: Submit Clicked',
      transform: transforms.inlineContractsSales.userData,
    },
    inline_contracts_sales_form_submit_error: {
      name: 'Inline Contracts Sales Form: Submit Error',
      transform: transforms.inlineContractsSales.userData,
    },
    inline_contracts_sales_form_submit_success: {
      name: 'Inline Contracts Sales Form: Submit Success',
      transform: transforms.inlineContractsSales.userData,
    },
  },
  objects_liquid: {
    generate_clicked: {
      name: 'AI: Objects Liquid: Generate with AI Clicked',
      transform: transforms.objectsLiquid.generateBaseData,
    },
    subject_copy_clicked: {
      name: 'AI: Objects Liquid: Subject Copy Clicked',
      transform: transforms.objectsLiquid.generateBaseData,
    },
    subject_copied: {
      name: 'AI: Objects Liquid: Subject Copied',
      transform: transforms.objectsLiquid.generateBaseData,
    },
    body_copy_clicked: {
      name: 'AI: Objects Liquid: Body Copy Clicked',
      transform: transforms.objectsLiquid.generateBaseData,
    },
    body_copied: {
      name: 'AI: Objects Liquid: Body Copied',
      transform: transforms.objectsLiquid.generateBaseData,
    },
    adjust_clicked: {
      name: 'AI: Objects Liquid: Adjust Message',
      transform: transforms.objectsLiquid.generateBaseData,
    },
  },
  renderer: {
    version_change_started: {
      // eslint-disable-next-line spellcheck/spell-checker
      name: 'Renderer: Version Change Started',
      transform: transforms.renderer.versionChangeStarted,
    },
    version_change_completed: {
      // eslint-disable-next-line spellcheck/spell-checker
      name: 'Renderer: Version Change Completed',
      transform: transforms.renderer.versionChangeCompleted,
    },
  },
  ui_test: {
    variant_a: {
      name: 'UI Test: A Variant event',
      transform: transforms.uiTest.variantA,
    },
    variant_b: {
      name: 'UI Test: B Variant event',
      transform: transforms.uiTest.variantB,
    },
  },
  product_activation: {
    activation_started: {
      name: 'Product Activation Started',
      transform: transforms.productActivation.activationStarted,
    },
    activation_completed: {
      name: 'Product Activation Completed',
      transform: transforms.productActivation.activationCompleted,
    },
    activation_step_completed: {
      name: 'Product Activation Step Completed',
      transform: transforms.productActivation.activationStepCompleted,
    },
  },
  performance: {
    signup: {
      name: 'Performance: Signup',
      transform: transforms.performance.signupDelay,
    },
    initial_load: {
      name: 'Performance: Initial Load',
      transform: transforms.performance.initialLoad,
    },
  },
  test_delivery_mode: {
    popover_opened: {
      name: 'Test Delivery Mode: Popover Opened',
      transform: transforms.testDeliveryMode.popoverOpened,
    },
    popover_cta_clicked: {
      name: 'Test Delivery Mode: Popover CTA Clicked',
      transform: transforms.testDeliveryMode.popoverCtaClicked,
    },
  },
};
